import type { CustomerContact } from '@clockwise/clients/customer-contacts';
import type { CustomerExpense } from '@clockwise/clients/customer-expenses';
import type {
  CreateInvoiceInput,
  Invoice,
  InvoiceLineAllocation,
  InvoiceLineItem,
  PatchInvoiceInput,
} from '@clockwise/clients/invoices';
import {
  ApiError,
  billToContactName,
  buildBillToAddressLines,
  CUSTOMER_EXPENSE_SOURCE,
  computeInvoiceTotals,
  deriveServicePeriodLabel,
  INVOICE_LINE_KIND_LABELS,
  INVOICE_LINE_KINDS,
  INVOICE_RECIPIENT_KINDS,
  type InvoiceDocument,
  type InvoiceLineKind,
  type InvoiceRecipient,
  invoiceEmailValues,
  isInvoiceContentEditable,
  isInvoiceEditable,
  isMoney,
  isoDateString,
  MAX_INVOICE_EMAIL_BODY_CHARS,
  normalizeInvoiceRecipients,
  recipientAddresses,
  recipientsFromContact,
  servicePeriodEndFromInclusive,
  servicePeriodEndToInclusive,
  tryRenderInvoiceEmailBody,
  tryRenderInvoiceEmailSubject,
} from '@clockwise/shared';
import { Combobox, DatePicker } from '@clockwise/ui';
import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToParentElement, restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import {
  ArrowLeft,
  ArrowRight,
  Download,
  Eye,
  GripVertical,
  Loader2,
  Lock,
  MoreHorizontal,
  Pencil,
  Plus,
  Send,
  Split,
  Trash2,
  Undo2,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { type FieldErrors, type UseFormReturn, useFieldArray, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';
import { CcEmailsField } from '@/components/custom/cc-emails-field';
import { ConfirmDialog } from '@/components/custom/confirm-dialog';
import { CustomerAutocomplete } from '@/components/custom/customer-autocomplete';
import { CustomerExpensePicker } from '@/components/custom/customer-expense-picker';
import { InvoiceAttachments } from '@/components/custom/invoice-attachments';
import { InvoiceAttention, invoiceBlockers } from '@/components/custom/invoice-attention';
import {
  InvoiceEmailBadge,
  InvoiceStatusBadge,
  InvoiceTypeBadge,
} from '@/components/custom/invoice-badges';
import { InvoiceDocumentPreview } from '@/components/custom/invoice-document-preview';
import {
  addSplitRow,
  allocationsForLine,
  LineAccountCell,
  LineSplitRows,
  rescaleAllocations,
  toAllocationInput,
  unbookedLineNumbers,
  useDefaultAccountForKind,
} from '@/components/custom/invoice-line-accounts';
import { derivedAmount, hasQuantityAndRate } from '@/components/custom/invoice-money';
import { canSendInvoice, InvoiceSendDialog } from '@/components/custom/invoice-send-dialog';
import { SEND_MODE_LABELS, SEND_MODES, type SendMode } from '@/components/custom/invoice-send-mode';
import {
  daysBetweenIso,
  daysForTerms,
  dueDateFor,
  InvoiceTermsInput,
  type TermsPreset,
} from '@/components/custom/invoice-terms-input';
import {
  PillTabs,
  PillTabsContent,
  PillTabsList,
  PillTabsTrigger,
} from '@/components/custom/pill-tabs';
import { PageShell } from '@/components/layout/page-shell';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { useEscapeToGoBack } from '@/hooks/use-escape-to-go-back';
import { usePermissionGuard } from '@/hooks/use-permission-guard';
import { useUnsavedRouteGuard } from '@/hooks/use-unsaved-route-guard';
import { customerContactsApi, invoicesApi } from '@/lib/api';
import { triggerDownload } from '@/lib/download';
import { lastSearch } from '@/lib/last-search';
import { useHasPermission } from '@/lib/permissions';
import { queryKeys } from '@/lib/query-keys';
import { todayIso } from '@/lib/user-display';
import { cn, formatInstantDate } from '@/lib/utils';

/**
 * Signed money, at most two decimals. Signed because a discount, credit or
 * write-off IS a line with a negative amount — there is no separate discount
 * field on an invoice (see docs/invoices.md).
 */
const AMOUNT_REGEX = /^-?\d+(\.\d{1,2})?$/;
/** Quantities are never negative; a refund is a negative amount, not a
 *  negative quantity. */
const QUANTITY_REGEX = /^\d+(\.\d{1,2})?$/;

/**
 * Client-side rules mirror the server's so a bad figure lands inline under the
 * offending input rather than arriving as a 400 the user has to decode (G12).
 *
 * Every monetary field is a STRING and stays one all the way to the API. The
 * columns are `numeric(12,2)`; parsing to a JS number here would reintroduce
 * exactly the binary-float rounding that column type exists to prevent.
 */
const LineSchema = z.object({
  kind: z.enum(INVOICE_LINE_KINDS),
  description: z.string().trim().min(1, 'Required').max(500, 'Up to 500 characters'),
  quantity: z
    .string()
    .trim()
    // Short: each sits under its own narrow cell. And each names the rule it
    // enforces — "Enter an amount" under a rate of 1.234 told nobody why.
    .regex(QUANTITY_REGEX, '0 or more, up to 2 decimals')
    .or(z.literal('')),
  rate: z.string().trim().regex(AMOUNT_REGEX, 'Up to 2 decimals').or(z.literal('')),
  amount: z.string().trim().min(1, 'Required').regex(AMOUNT_REGEX, 'Up to 2 decimals'),
  /** Provenance, carried through the form so a re-save doesn't strip the link
   *  back to the customer expense this line was billed from. */
  sourceType: z.string().nullable(),
  sourceId: z.string().nullable(),
  /** Which agreement this line was sold under, and the SOW version that priced
   *  it. Carried for the same reason and more urgently: `lineItems` is a
   *  WHOLE-COLLECTION replace, so a line saved without these loses them, and a
   *  document gathering several engagements would forget which fee belonged to
   *  which the first time anyone touched it. */
  engagementId: z.string().nullable(),
  sowVersion: z.number().nullable(),
  /** Where the line's money is booked — finance-only, never printed. None, one
   *  account for the whole line, or a split. See `invoice-line-accounts`. */
  allocations: z
    .array(
      z.object({
        accountId: z.string().nullable(),
        amount: z.string().trim().regex(AMOUNT_REGEX, 'Up to 2 decimals'),
      }),
    )
    // A split row must name its account before the invoice is saved; whether
    // the rows add up is approval's question, not a save's.
    .superRefine((rows, ctx) => {
      if (rows.length < 2) return;
      rows.forEach((r, i) => {
        if (!r.accountId) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [i, 'accountId'],
            message: 'Pick an account or remove this row',
          });
        }
      });
    }),
});
type LineValues = z.infer<typeof LineSchema>;

/** What the renderer's Bill To panel prints, and what the API accepts. */
const MAX_BILL_TO_LINES = 6;
const MAX_BILL_TO_LINE_CHARS = 120;

/**
 * The address block moves between one textarea and the API's `string[]`.
 *
 * Blank lines are dropped rather than sent: the array is "the lines that
 * print", and an empty one would be a blank row on a customer's invoice — the
 * same reason `buildAddressLines` drops empty segments server-side. Dropping
 * them here also means a stray trailing newline is harmless.
 */
export const addressTextToLines = (text: string): string[] =>
  text
    .split('\n')
    .map((l) => l.trim())
    .filter((l) => l !== '');

const addressLinesToText = (lines: readonly string[]): string => lines.join('\n');

/**
 * The Bill To block, as the form holds it.
 *
 * Keyed `billTo.*` to match the API's own field paths, so a server-side field
 * error lands on the input that caused it with no translation table in
 * between — see `isFormField` and `boundInput`.
 *
 * `name` is the COMPANY the invoice is billed to; the contact's own name is
 * the first line of the address block, which is how the server composes it
 * (`buildAddressLines`) and how the reference invoice reads.
 */
const BillToSchema = z.object({
  name: z.string().trim().min(1, 'Required').max(120, 'Up to 120 characters'),
  addressLines: z
    .string()
    .refine(
      (v) => addressTextToLines(v).length <= MAX_BILL_TO_LINES,
      `Up to ${MAX_BILL_TO_LINES} lines — the invoice prints no more than that`,
    )
    .refine(
      (v) => addressTextToLines(v).every((l) => l.length <= MAX_BILL_TO_LINE_CHARS),
      `Each line can be up to ${MAX_BILL_TO_LINE_CHARS} characters`,
    ),
  /** Everyone the invoice is emailed to, To and CC, in order. Filled from the
   *  contact and editable per invoice; with no To it cannot be approved or
   *  sent. Each address is checked as it is added (`CcEmailsField`). Delivery
   *  only — never printed. */
  recipients: z.array(z.object({ email: z.string(), kind: z.enum(INVOICE_RECIPIENT_KINDS) })),
  contactId: z.string().nullable(),
});

/** Notes are for the team: neither the PDF nor the email carries them. */
const NOTES_HINT = 'Internal. Not printed on the invoice or emailed.';

const FormObject = z.object({
  /** Unused in create mode, where the server allocates the number. */
  invoiceNumber: z.string().trim().max(64, 'Up to 64 characters'),
  invoiceDate: isoDateString('Required'),
  dueDate: isoDateString().or(z.literal('')),
  terms: z.string().trim().max(60, 'Up to 60 characters'),
  servicePeriodStart: isoDateString().or(z.literal('')),
  servicePeriodEnd: isoDateString().or(z.literal('')),
  sendMode: z.enum(SEND_MODES),
  /** Only meaningful when `sendMode` is `date`. */
  scheduledSendOn: isoDateString().or(z.literal('')),
  notes: z.string().trim().max(4000, 'Up to 4000 characters'),
  /** This invoice's own email message, or null to send the invoice-settings
   *  template filled in. Only an edit makes it non-null, so an invoice nobody
   *  touched the message on stores nothing. */
  emailBody: z
    .string()
    .max(MAX_INVOICE_EMAIL_BODY_CHARS, `Up to ${MAX_INVOICE_EMAIL_BODY_CHARS} characters`)
    .nullable(),
  billTo: BillToSchema,
  lineItems: z.array(LineSchema),
});
type FormValues = z.infer<typeof FormObject>;

const requireSendDate = (
  v: { sendMode: SendMode; scheduledSendOn: string },
  ctx: z.RefinementCtx,
) => {
  if (v.sendMode === 'date' && v.scheduledSendOn === '') {
    ctx.addIssue({ code: 'custom', path: ['scheduledSendOn'], message: 'Pick a send date' });
  }
};

const FormSchema = FormObject.superRefine(requireSendDate);

/** Edit mode: the number is on screen and may be changed, so it is required. */
const EditFormSchema = FormObject.extend({
  invoiceNumber: z.string().trim().min(1, 'Required').max(64, 'Up to 64 characters'),
}).superRefine(requireSendDate);

export function toFormValues(
  invoice: Invoice,
  lineItems: InvoiceLineItem[],
  allocations: readonly InvoiceLineAllocation[] = [],
): FormValues {
  return {
    invoiceDate: invoice.invoiceDate,
    dueDate: invoice.dueDate ?? '',
    terms: invoice.terms ?? '',
    servicePeriodStart: invoice.servicePeriodStart ?? '',
    servicePeriodEnd: invoice.servicePeriodEnd ?? '',
    sendMode: invoice.sendOnApproval ? 'approval' : invoice.scheduledSendOn ? 'date' : 'manual',
    scheduledSendOn: invoice.scheduledSendOn ?? '',
    notes: invoice.notes ?? '',
    emailBody: invoice.emailBody ?? null,
    invoiceNumber: invoice.invoiceNumber,
    billTo: {
      name: invoice.billToName,
      addressLines: addressLinesToText(invoice.billToAddressLines),
      recipients: invoice.recipients,
      contactId: invoice.billToContactId,
    },
    lineItems: lineItems.map((l) => ({
      kind: l.kind,
      description: l.description,
      quantity: l.quantity ?? '',
      rate: l.rate ?? '',
      amount: l.amount,
      sourceType: l.sourceType,
      sourceId: l.sourceId,
      engagementId: l.engagementId,
      sowVersion: l.sowVersion,
      allocations: allocationsForLine(l.id, l.amount, allocations),
    })),
  };
}

/**
 * The form's own field names, so a server error can be told apart from one
 * naming something this form has no input for. Nested line paths
 * (`lineItems.2.amount`) count — they resolve to a rendered row.
 */
const FORM_FIELDS = [
  'invoiceDate',
  'dueDate',
  'terms',
  'servicePeriodStart',
  'servicePeriodEnd',
  'sendMode',
  'scheduledSendOn',
  'invoiceNumber',
  'notes',
  'emailBody',
  'billTo',
  'lineItems',
] as const;

function isFormField(path: string): boolean {
  const root = path.split('.')[0] ?? '';
  return (FORM_FIELDS as readonly string[]).includes(root);
}

/** The visible label of each field, for a message that has to name its field
 *  because it cannot sit under it. Mirrors the `Field label=` props below, and
 *  the `Line N <field>` shape of the per-line `aria-label`s. */
const FIELD_LABELS: Record<string, string> = {
  invoiceDate: 'Invoice date',
  dueDate: 'Due date',
  terms: 'Terms',
  servicePeriodStart: 'Service period start',
  servicePeriodEnd: 'Service period end',
  sendMode: 'Send',
  scheduledSendOn: 'Send date',
  sendOnApproval: 'Send',
  notes: 'Notes',
  emailBody: 'Email message',
  lineItems: 'Line items',
  billTo: 'Invoice to',
  // Keyed by full path: the server names these individually
  // (`billTo.contactId` when a contact isn't this customer's), and "Bill to"
  // alone would not tell an operator which of the three inputs to look at.
  'billTo.name': 'Invoice to name',
  'billTo.addressLines': 'Invoice to address',
  'billTo.contactId': 'Billing contact',
  'billTo.recipients': 'Recipients',
  invoiceNumber: 'Invoice number',
};

function describeFieldPath(path: string): string {
  const [root = '', index, key] = path.split('.');
  // Not every segment under `lineItems` is a row number: an error on the array
  // itself is nested under a literal `root` key, which would number a line NaN.
  if (root === 'lineItems' && index !== undefined && Number.isInteger(Number(index))) {
    const line = `Line ${Number(index) + 1}`;
    return key === undefined ? line : `${line} ${key}`;
  }
  return FIELD_LABELS[path] ?? FIELD_LABELS[root] ?? root;
}

type FieldErrorLeaf = { path: string; message: string };

/** Flatten react-hook-form's error tree to `path`/`message` leaves. The order
 *  is the resolver's — schema declaration order, which is NOT render order
 *  (`notes` is declared above `lineItems` but renders below the whole grid),
 *  so anything that cares about what comes first on screen sorts by the DOM. */
function collectErrorLeaves(node: unknown, path: string, out: FieldErrorLeaf[]): void {
  if (node === null || typeof node !== 'object') return;
  const { message } = node as { message?: unknown };
  if (typeof message === 'string' && message !== '') {
    out.push({ path, message });
    return;
  }
  for (const [key, child] of Object.entries(node)) {
    // `ref` is the DOM node the error came from — descending into it would
    // walk the whole document — and `types` holds the other rules that failed,
    // not fields.
    if (key === 'ref' || key === 'types') continue;
    collectErrorLeaves(child, path === '' ? key : `${path}.${key}`, out);
  }
}

/**
 * The input `register`ed under `path`, or null when the path has none to land
 * on: the dates are `setValue`d through a {@link DatePicker}, and a line's Qty
 * and Rate are not rendered at all for a kind that has neither. Exactly one of
 * the card and table layouts is mounted at a time, so one match is all there
 * is to find. (`CSS.escape` is dnd-kit's transform helper in this file, but a
 * field path only ever holds letters, digits and dots.)
 */
function boundInput(path: string): HTMLElement | null {
  return document.querySelector<HTMLElement>(`[name="${path}"]`);
}

/** True when `a` renders before `b` — the two are always in the same document
 *  here, so the one bit is the whole answer. */
function precedes(a: HTMLElement, b: HTMLElement): boolean {
  return (a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING) !== 0;
}

/**
 * Client-side validation is the one path on which pressing Save can produce
 * nothing at all: the mutation never runs, so its `onError` never gets to
 * explain itself, and the button just sits there. Same split as that handler —
 * a message whose input is on screen already renders beside it, so scroll the
 * topmost such input into view; a message with no input to sit under is
 * returned, for the caller to show in the error row under the header. Never a
 * toast: it would sit over the very buttons being used. A finance user should
 * never have to guess which line is wrong (G12).
 */
export function reportInvalidSubmit(errors: FieldErrors<FormValues>): string | null {
  const leaves: FieldErrorLeaf[] = [];
  collectErrorLeaves(errors, '', leaves);

  const unbound: string[] = [];
  // The topmost errored input as RENDERED. Taking the first leaf instead would
  // scroll past a bad line to reach an over-long Notes, which the schema
  // declares earlier but the page draws last.
  let topmost: HTMLElement | null = null;
  for (const leaf of leaves) {
    const input = boundInput(leaf.path);
    if (input === null) {
      unbound.push(`${describeFieldPath(leaf.path)}: ${leaf.message}`);
      continue;
    }
    if (topmost === null || precedes(input, topmost)) topmost = input;
  }

  if (topmost !== null) {
    topmost.scrollIntoView({ behavior: 'smooth', block: 'center' });
    // The scroll above is the one that should happen; focusing must not fight
    // it with a jump of its own.
    topmost.focus({ preventScroll: true });
  }

  if (unbound.length > 0) return unbound.join(' ');
  // Nothing to say and nowhere to look is the dead button this handler exists
  // to prevent, so say *something* rather than swallow the click.
  if (topmost === null) return 'Could not save the invoice — some fields need fixing.';
  return null;
}

/** Empty string means "cleared" for a nullable column, not "unchanged" — the
 *  patch schema takes `null` for that, so the two must not be conflated. */
const nullIfBlank = (v: string): string | null => (v.trim() === '' ? null : v.trim());

/** `YYYY-MM-DD` plus N days. UTC throughout, so a browser west of Greenwich
 *  doesn't land a day early. */
function addIsoDays(iso: string, days: number): string {
  const d = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return '';
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/**
 * Which Bill To keys were actually EDITED. RHF's `dirtyFields` shape, narrowed
 * to the keys this form owns.
 */
export type BillToDirty =
  | {
      name?: boolean;
      addressLines?: boolean;
      contactId?: boolean;
      recipients?: boolean;
    }
  | undefined;

/** True when any leaf of an RHF dirty-flags tree is set. A field that was
 *  edited and then put back can leave an empty `{}` or a `[false]` behind, and
 *  a key's mere presence would read that as an edit. */
const anyTrue = (v: unknown): boolean =>
  v === true || (typeof v === 'object' && v !== null && Object.values(v).some(anyTrue));

/** When the invoice gets emailed is bookkeeping ABOUT it, not content. */
const SEND_KEYS = new Set(['sendMode', 'scheduledSendOn']);

/**
 * The kinds a line's type dropdown offers. `adjustment` stays a valid kind —
 * imports accept it and an older line may carry it — but it is no longer
 * offered as a pick. A line that already is one keeps it listed, or its
 * select would render blank.
 */
export function pickableLineKinds(current: InvoiceLineKind): InvoiceLineKind[] {
  return INVOICE_LINE_KINDS.filter((k) => k !== 'adjustment' || k === current);
}

/**
 * Whether the unsaved edits change what the invoice SAYS.
 *
 * When to send is bookkeeping ABOUT the invoice, not content — the server
 * leaves it out of `touchesContent` so scheduling an approved invoice keeps
 * the approval. But this editor sends every field on every save, and the
 * server counts any content key it receives as an edit. So when this is
 * false, the save must send the schedule ALONE (see the save mutation), and
 * the "saving returns this to draft" warning must not show.
 *
 * `alsoChanged` carries edits RHF's flags can't be trusted to report: the
 * recipient list compared by value.
 */
export function contentDirty(dirty: object, alsoChanged: boolean): boolean {
  return alsoChanged || Object.entries(dirty).some(([key, v]) => !SEND_KEYS.has(key) && anyTrue(v));
}

/**
 * What will actually happen, said plainly: nothing is emailed before the
 * invoice is approved, and a date's send time comes from invoice settings.
 */
export function sendOnHint(
  mode: SendMode,
  date: string,
  status: Invoice['status'] | null,
): string | undefined {
  if (mode === 'manual') return undefined;
  if (mode === 'approval') {
    return status === 'approved'
      ? 'Emailed within about 15 minutes.'
      : 'Emailed within about 15 minutes of being approved.';
  }
  if (date === '') return undefined;
  if (status === 'approved') {
    return 'Emailed automatically on this date, at the send time set in invoice settings.';
  }
  return 'Emailed on this date once the invoice is approved.';
}

/** The earliest sensible send date: today, or the invoice date if later. */
const firstSendDate = (invoiceDate: string): string => {
  const today = todayIso();
  return invoiceDate > today ? invoiceDate : today;
};

export const sameList = (a: readonly (string | undefined)[], b: readonly (string | undefined)[]) =>
  a.length === b.length && a.every((v, i) => v === b[i]);

/** Same addresses, same order, same kind each. `b` is loose because it is
 *  read from RHF's deep-partial `defaultValues`. */
export const sameRecipients = (
  a: readonly InvoiceRecipient[],
  b: readonly ({ email?: string; kind?: string } | undefined)[],
) => a.length === b.length && a.every((r, i) => r.email === b[i]?.email && r.kind === b[i]?.kind);

/**
 * The Bill To override, built from what the operator changed — never from
 * every field on the form.
 *
 * The server MERGES: an omitted key keeps what the invoice already holds (or,
 * on create, what the server derives). Picking a contact fills the address
 * and the recipients in the form, so both arrive dirty with it and the server
 * stores exactly what the operator saw.
 */
export function toBillToPatch(values: FormValues, dirty: BillToDirty): PatchInvoiceInput['billTo'] {
  const billTo: NonNullable<PatchInvoiceInput['billTo']> = {};
  if (dirty?.name) billTo.name = values.billTo.name.trim();
  if (dirty?.addressLines) billTo.addressLines = addressTextToLines(values.billTo.addressLines);
  if (dirty?.recipients) billTo.recipients = normalizeInvoiceRecipients(values.billTo.recipients);
  if (dirty?.contactId) billTo.contactId = values.billTo.contactId;
  return Object.keys(billTo).length > 0 ? billTo : undefined;
}

/** The two API fields behind the one Send choice. */
function toSendPatch(values: FormValues): {
  scheduledSendOn: string | null;
  sendOnApproval: boolean;
} {
  return {
    scheduledSendOn: values.sendMode === 'date' ? nullIfBlank(values.scheduledSendOn) : null,
    sendOnApproval: values.sendMode === 'approval',
  };
}

export function toPatch(
  values: FormValues,
  billTo?: PatchInvoiceInput['billTo'],
  invoiceNumber?: string,
): PatchInvoiceInput {
  return {
    invoiceNumber,
    billTo,
    invoiceDate: values.invoiceDate,
    dueDate: nullIfBlank(values.dueDate),
    terms: nullIfBlank(values.terms),
    servicePeriodStart: nullIfBlank(values.servicePeriodStart),
    servicePeriodEnd: nullIfBlank(values.servicePeriodEnd),
    ...toSendPatch(values),
    notes: nullIfBlank(values.notes),
    // Blank goes back to the template rather than emailing an empty message.
    emailBody: values.emailBody === null ? null : nullIfBlank(values.emailBody),
    // Sending `lineItems` REPLACES the whole collection — that is the API's
    // contract, and it is what the editor means: the rows on screen are the
    // rows on the invoice.
    lineItems: values.lineItems.map((l) => ({
      kind: l.kind,
      description: l.description,
      // A discount carries no qty/rate even if the fields once held values —
      // the inputs are hidden for that kind, so persisting stale numbers would
      // print a Qty on a document that shows none.
      quantity: hasQuantityAndRate(l.kind) ? nullIfBlank(l.quantity) : null,
      rate: hasQuantityAndRate(l.kind) ? nullIfBlank(l.rate) : null,
      amount: l.amount,
      sourceType: l.sourceType,
      sourceId: l.sourceId,
      engagementId: l.engagementId,
      sowVersion: l.sowVersion,
      allocations: toAllocationInput(l.allocations),
    })),
  };
}

/**
 * Build the document the preview renders, from the form's live values plus the
 * invoice's own snapshot columns (issuer, bill-to, remittance) — which the
 * editor never changes.
 *
 * Not `invoiceDocumentSchema.parse`: that requires at least one line and a
 * usable amount on each, and this runs on every keystroke against a form that
 * is legitimately half-typed. A blank amount previews as 0.00 here rather than
 * throwing, which is the opposite of the render path's rule (`document.ts`
 * refuses to print an invoice it cannot state the amounts for) — and correctly
 * so: this is a draft being typed, not a document being issued.
 */
type DocumentSnapshot = {
  issuer: Invoice['issuer'];
  invoiceNumber: string;
  currency: string;
  paymentDetails: string[];
};

/**
 * The create payload.
 *
 * Shares `toPatch`'s field mapping, but every nullable key is OMITTED when
 * blank rather than sent as null. On create the two are not the same thing: an
 * explicit null means "this invoice has no terms", while an absent key means
 * "apply the tenant default" — and the defaults are exactly what the form was
 * prefilled from, so a blank field here is a value the operator cleared on
 * purpose... except for the ones they never saw. Omitting is the safe reading:
 * the server's default is right more often than an empty string is.
 */
function toCreate(
  values: FormValues,
  customerId: string,
  billTo?: CreateInvoiceInput['billTo'],
): CreateInvoiceInput {
  const patch = toPatch(values);
  const optional = <T,>(v: T | null): T | undefined => v ?? undefined;
  return {
    customerId,
    // Absent unless the operator edited the block. The create screen PREFILLS
    // these from `newInvoiceBillTo`, so sending the prefill back would turn
    // a value the server was about to derive into a caller override that
    // outlives a later change to the customer record.
    billTo,
    invoiceDate: values.invoiceDate,
    dueDate: optional(patch.dueDate ?? null),
    terms: optional(patch.terms ?? null),
    servicePeriodStart: optional(patch.servicePeriodStart ?? null),
    servicePeriodEnd: optional(patch.servicePeriodEnd ?? null),
    scheduledSendOn: optional(patch.scheduledSendOn ?? null),
    // Always explicit: the operator saw the choice, so the server default
    // must not stand in for it.
    sendOnApproval: patch.sendOnApproval,
    notes: optional(patch.notes ?? null),
    emailBody: optional(patch.emailBody ?? null),
    lineItems: patch.lineItems,
  };
}

function buildPreviewDocument(snapshot: DocumentSnapshot, values: FormValues): InvoiceDocument {
  /**
   * Only values the decimal helpers can actually parse get through.
   *
   * This runs on RAW form state — `form.watch()`, not validated output, since
   * Zod runs on submit — so it sees every intermediate keystroke. `-` is the
   * first character of any negative discount and `Big` rejects it, as it does
   * `.` and `12a`. Letting one reach `computeInvoiceTotals` throws
   * `InvalidMoneyError` during render, which takes the whole editor route down
   * to the router's error component and loses every unsaved edit.
   *
   * Unparseable therefore reads as absent: an empty Qty/Rate cell, and an
   * amount that previews as 0.00 until the operator finishes typing. That is
   * the same degradation the float version gave, and it matches the guards
   * `derivedAmount` and `balanceDue` already apply.
   */
  const asMoney = (v: string): string | undefined => (isMoney(v) ? v.trim() : undefined);

  return {
    issuer: snapshot.issuer,
    // Read from the FORM, not the snapshot, so an edit shows up in the
    // document beside it as it is typed.
    billTo: {
      name: values.billTo.name,
      addressLines: addressTextToLines(values.billTo.addressLines),
      email: recipientAddresses(values.billTo.recipients, 'to')[0],
    },
    // The typed number in edit mode, so a renumber shows in the document as it
    // is typed; create mode has no field and prints the expected number.
    invoiceNumber: values.invoiceNumber.trim() || snapshot.invoiceNumber,
    invoiceDate: values.invoiceDate,
    dueDate: values.dueDate || undefined,
    terms: values.terms || undefined,
    // Derived, exactly as the server derives it when it renders the real
    // document — so the pane and the PDF print the same period.
    servicePeriod:
      deriveServicePeriodLabel(values.servicePeriodStart, values.servicePeriodEnd) ?? undefined,
    currency: snapshot.currency,
    paymentDetails: snapshot.paymentDetails,
    lineItems: values.lineItems.map((l) => ({
      description: l.description,
      quantity: hasQuantityAndRate(l.kind) ? asMoney(l.quantity) : undefined,
      rate: hasQuantityAndRate(l.kind) ? asMoney(l.rate) : undefined,
      amount: asMoney(l.amount) ?? '0.00',
    })),
  } as InvoiceDocument;
}

/**
 * The invoice editor, in both of its modes.
 *
 * `invoiceId === null` is CREATE: nothing exists on the server yet, so the
 * screen holds everything client-side and POSTs once on save. That is the
 * whole reason create lives here rather than behind a "new invoice" dialog
 * that raises an empty draft first — an invoice number is allocated on
 * create and `InvoiceNumberSequenceRepository.allocate` only ever moves
 * forward within a month, so a draft raised and abandoned burns a number
 * permanently and leaves a gap in that month nobody can account for later.
 *
 * Everything below the header is deliberately shared between the two modes:
 * two copies of the line editor is exactly how a discount comes to hide its
 * Rate on one screen and not the other.
 */
export function InvoiceEditor({ invoiceId }: { invoiceId: string | null }) {
  const isNew = invoiceId === null;
  // Safe to use as a query key and in mutations only where `isNew` is false;
  // every such call site is guarded by it.
  const id = invoiceId ?? '';
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { allowed: canView, fallback } = usePermissionGuard('invoice.view', 'invoices');
  const canEdit = useHasPermission('invoice.edit');
  const canApprove = useHasPermission('invoice.approve');
  const canSend = useHasPermission('invoice.send');
  const canAddContact = useHasPermission('customer.contact.edit');
  /** The Send dialog: who to email, then the send itself. */
  const [sendOpen, setSendOpen] = useState(false);
  /** Set just before a deliberate redirect, so the unsaved-changes guard lets
   *  it through instead of prompting about a form the user is done with. */
  const savedRef = useRef(false);
  /** Edit, or the PDF preview. A tab rather than half the screen: most
   *  invoices here come out of generation and are reviewed, so the lines get
   *  the width and the document is one click away. */
  const [view, setView] = useState<'edit' | 'preview'>('edit');
  /** The Bill To block is set once and rarely touched, so it is a one-line
   *  summary until someone asks to change it. */
  const [billToOpen, setBillToOpen] = useState(false);
  /** Whether the Email to input is open — lifted here so the checklist's
   *  "Add email" can open it from across the page. */
  const [addingEmail, setAddingEmail] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmUnlock, setConfirmUnlock] = useState(false);
  // What the last action was refused with, when no field on the page can
  // hold the message. Shown under the header, in place of a toast.
  const [actionError, setActionError] = useState<string | null>(null);

  /** Create mode only — which customer the invoice is being raised for. */
  const [customerId, setCustomerId] = useState<string | null>(null);

  const detail = useQuery({
    queryKey: queryKeys.invoices.detail(id),
    queryFn: () => invoicesApi.get(id),
    enabled: canView && !isNew,
  });

  // Create mode's stand-in for the snapshot columns an existing invoice
  // carries: issuer, remittance, currency and the expected number. Tenant-
  // level and customer-independent, so it loads the instant the screen
  // opens — before any customer is picked. Resolved server-side so the
  // preview draws what `create` will bake in.
  const defaults = useQuery({
    queryKey: queryKeys.invoices.newDefaults(),
    queryFn: () => invoicesApi.newDefaults(),
    enabled: isNew,
  });

  // The customer-specific half of the same preview: the Bill To block.
  // Separate query because it's the only part that needs a customer.
  const billToDefaults = useQuery({
    queryKey: queryKeys.invoices.newBillTo(customerId ?? ''),
    queryFn: () => invoicesApi.newBillTo(customerId as string),
    enabled: isNew && customerId !== null,
  });

  const invoice = detail.data?.data ?? null;
  const lineItems = detail.data?.related.lineItems ?? [];
  const lineAllocations = detail.data?.related.allocations ?? [];
  const newDefaults = defaults.data?.data ?? null;
  const newBillTo = billToDefaults.data?.data ?? null;
  /** The tenant half failed with nothing to show. Only then: a failed
   *  background refetch keeps the last good copy, which stays usable. */
  const defaultsUnavailable = isNew && defaults.isError && !newDefaults;

  /** Whose contacts the billing-contact picker offers. Known before the
   *  invoice loads in create mode, and from the invoice itself in edit mode —
   *  an invoice can never be re-pointed at another customer. */
  const billToCustomerId = invoice?.customerId ?? customerId ?? '';

  // Every active contact on the customer. A plain list rather than a
  // typeahead: the endpoint takes no `q` and a customer has a handful of
  // contacts, not a catalog.
  const contacts = useQuery({
    queryKey: queryKeys.customerContacts.list(billToCustomerId),
    queryFn: () => customerContactsApi.list(billToCustomerId),
    enabled: canView && billToCustomerId !== '',
  });

  const form = useForm<FormValues>({
    resolver: zodResolver(invoiceId === null ? FormSchema : EditFormSchema),
    // `reportInvalidSubmit` owns revealing the first bad field. Left on, this
    // runs straight after it and focuses the first errored field in
    // REGISTRATION order — a different one than the schema-ordered walk picks
    // whenever Notes and a line both fail — with a plain `focus()` whose
    // default scroll aborts the smooth one mid-flight.
    shouldFocusError: false,
    defaultValues: {
      invoiceDate: invoiceId === null ? todayIso() : '',
      dueDate: '',
      terms: '',
      servicePeriodStart: '',
      servicePeriodEnd: '',
      // New invoices go out on approval; an existing one is re-seeded below.
      sendMode: 'approval',
      scheduledSendOn: '',
      notes: '',
      emailBody: null,
      invoiceNumber: '',
      billTo: {
        name: '',
        addressLines: '',
        recipients: [],
        contactId: null,
      },
      lineItems: [],
    },
  });
  const lines = useFieldArray({ control: form.control, name: 'lineItems' });
  // The invoice-settings default account per line kind, for new lines.
  const defaultAccountFor = useDefaultAccountForKind();

  // Re-seed the form whenever the server's copy changes — on load, and after
  // every save, so the fields show what was actually stored rather than what
  // was typed (the server renumbers lines and recomputes the total).
  const reset = form.reset;
  useEffect(() => {
    if (!invoice) return;
    reset(toFormValues(invoice, lineItems, lineAllocations));
    // `updatedAt` stands in for the whole payload: it changes on every write
    // and nothing else does without it.
  }, [invoice?.id, invoice?.updatedAt, reset]);

  // Create mode: copy the terms into the form once the defaults land — the
  // customer's credit term when it has one, else the tenant's, the same order
  // the server applies. Prefilled rather than left blank and derived on the
  // server, so the preview shows the terms and due date the saved invoice will
  // actually carry — and so the operator can override either before saving.
  //
  // Only fields the user hasn't touched are written, or reselecting a customer
  // would discard an edit they had already made.
  const setValue = form.setValue;
  const getFieldState = form.getFieldState;
  useEffect(() => {
    if (!isNew || !newDefaults) return;
    if (!getFieldState('terms').isDirty) {
      setValue('terms', newBillTo?.terms ?? newDefaults.terms ?? '');
    }
  }, [isNew, newDefaults, newBillTo, setValue, getFieldState]);

  // The Bill To block this customer's invoice would snapshot right now.
  // Seeded WITHOUT `shouldDirty` on purpose: an untouched prefill must stay
  // clean, so `toBillToPatch` omits it and the server derives the block
  // itself rather than receiving the preview back as a caller override.
  useEffect(() => {
    if (!isNew || !newBillTo) return;
    if (!getFieldState('billTo.name').isDirty) {
      setValue('billTo.name', newBillTo.billTo.name);
    }
    if (!getFieldState('billTo.addressLines').isDirty) {
      setValue('billTo.addressLines', addressLinesToText(newBillTo.billTo.addressLines));
    }
    // Not over a hand-set recipient list. The contact is only ever on the
    // invoice as the SOURCE of the addresses it is mailed to — the block is
    // printed with the contact's name as its first line. Naming one whose
    // address the operator has already replaced would print the document to
    // one person and mail it to another, and would leave To empty (and
    // approval blocked on "No billing email") whenever what they typed was a
    // CC. `recipients` below is skipped for the same reason, one field later.
    if (!getFieldState('billTo.contactId').isDirty && !getFieldState('billTo.recipients').isDirty) {
      setValue('billTo.contactId', newBillTo.billTo.contactId);
    }
    if (!getFieldState('billTo.recipients').isDirty) {
      setValue('billTo.recipients', newBillTo.billTo.recipients);
    }
  }, [isNew, newBillTo, setValue, getFieldState]);

  // Due date follows the invoice date while the operator leaves it alone —
  // changing the invoice date on a Net-30 invoice should move the due date
  // with it, not silently keep a date derived from the old one.
  const invoiceDateValue = form.watch('invoiceDate');
  const termsValue = form.watch('terms');
  useEffect(() => {
    if (!isNew) return;
    if (getFieldState('dueDate').isDirty) return;
    const days = daysForTerms(termsValue);
    // Terms with no day count leave the box empty, as the server would —
    // not holding a date derived from the previous customer's terms.
    setValue('dueDate', days === null ? '' : dueDateFor(invoiceDateValue, days));
  }, [isNew, invoiceDateValue, termsValue, setValue, getFieldState]);

  // Content locks at approval: changing an approved invoice means returning it
  // to draft first. Only when to send stays open until it is sent.
  const editable = isNew
    ? canEdit
    : invoice
      ? isInvoiceContentEditable(invoice.status) && canEdit
      : false;
  const sendEditable = isNew
    ? canEdit
    : invoice
      ? isInvoiceEditable(invoice.status) && canEdit
      : false;

  // Block navigating away from unsaved edits. Read live, and cleared by the
  // delete path's `savedRef` so its redirect isn't caught by a prompt about
  // changes to an invoice that no longer exists.
  const { blockerDialog } = useUnsavedRouteGuard(
    () => form.formState.isDirty && editable && !savedRef.current,
    undefined,
    // Only arm the browser's native "Leave site?" on a genuinely dirty form —
    // a read-only view of a sent invoice should refresh without a prompt.
    () => form.formState.isDirty && editable && !savedRef.current,
  );

  const save = useMutation({
    mutationFn: (values: FormValues) => {
      // Read at submit time, not from a closure over an earlier render: the
      // dirty set decides which keys the patch names.
      const dirtyBillTo = form.formState.dirtyFields.billTo;
      // Sent only when edited: an unchanged number must not be checked for
      // duplicates against itself, nor move the month's counter.
      const renumber = form.formState.dirtyFields.invoiceNumber ? values.invoiceNumber : undefined;
      // By value, against what the form was seeded with: RHF's per-index flags
      // for an array can't reliably say "the last one was removed".
      const recipientsChanged = !sameRecipients(
        values.billTo.recipients,
        form.formState.defaultValues?.billTo?.recipients ?? [],
      );
      const billTo = toBillToPatch(values, {
        name: dirtyBillTo?.name,
        addressLines: dirtyBillTo?.addressLines,
        contactId: dirtyBillTo?.contactId,
        recipients: recipientsChanged,
      });
      if (isNew) return invoicesApi.create(toCreate(values, customerId as string, billTo));
      // Only when to send changed: send it ALONE. The full patch names every
      // content field, which the server counts as an edit and would return an
      // approved invoice to draft — the one thing a schedule must never do.
      if (!contentDirty(form.formState.dirtyFields, recipientsChanged)) {
        return invoicesApi.patch(id, toSendPatch(values));
      }
      return invoicesApi.patch(id, toPatch(values, billTo, renumber));
    },
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.invoices.all });
      if (isNew) {
        // Let the redirect past the unsaved-changes guard: the form is dirty
        // by definition here, and the invoice it describes now exists.
        savedRef.current = true;
        toast.success(`Invoice ${res.data.invoiceNumber} created`);
        navigate({ to: '/invoices/$id', params: { id: res.data.id } });
        return;
      }
      toast.success('Invoice saved');
      queryClient.invalidateQueries({ queryKey: queryKeys.invoices.detail(id) });
    },
    onMutate: () => setActionError(null),
    onError: (err) => {
      // Field-level errors go under the input that caused them; anything else
      // goes in the error row under the header. A finance user should never
      // have to guess which line is wrong (G12).
      //
      // Only paths that are actually bound to an input may skip the row.
      // `setError` on an unbound key stores a message nothing renders, so the
      // errors that matter most on create — `invoiceSettings` when the tenant
      // was never configured, `customerId` (React state, not a form field) —
      // would leave the button spinning down onto an unchanged screen with no
      // sign the click did anything at all.
      if (err instanceof ApiError && err.fields?.length) {
        const unbound: string[] = [];
        for (const f of err.fields) {
          if (isFormField(f.path)) {
            form.setError(f.path as keyof FormValues, { message: f.message });
          } else {
            unbound.push(f.message);
          }
        }
        if (err.fields.some((f) => /^billTo\.(name|addressLines|contactId)$/.test(f.path))) {
          setBillToOpen(true);
        }
        if (unbound.length > 0) setActionError(unbound.join(' '));
        return;
      }
      setActionError(err instanceof ApiError ? err.message : 'Could not save the invoice');
    },
  });

  const transition = useMutation({
    mutationFn: (action: 'approve' | 'unapprove') =>
      action === 'approve'
        ? invoicesApi.approve({ ids: [id], reviewed: true })
        : invoicesApi.unapprove(id),
    onMutate: () => setActionError(null),
    onSuccess: ({ data }, action) => {
      // These endpoints are bulk and report per-row outcomes, so a 200 does
      // NOT mean the transition happened. Read the row back before claiming it.
      const failure = data.failed[0];
      if (failure) {
        setActionError(failure.message);
        return;
      }
      toast.success(action === 'approve' ? 'Invoice approved' : 'Invoice unlocked');
      queryClient.invalidateQueries({ queryKey: queryKeys.invoices.detail(id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.invoices.all });
    },
    onError: (err) =>
      setActionError(err instanceof ApiError ? err.message : 'Could not update status'),
  });

  const remove = useMutation({
    mutationFn: () => invoicesApi.remove(id),
    onMutate: () => setActionError(null),
    onSuccess: () => {
      // Let the redirect through the unsaved-changes guard: the form may still
      // be dirty, but the invoice it was editing is gone.
      savedRef.current = true;
      toast.success('Invoice deleted');
      queryClient.invalidateQueries({ queryKey: queryKeys.invoices.all });
      // Back to the list as it was — minus `page`, which may be past the end now
      // that this invoice is gone.
      navigate({ to: '/invoices', search: lastSearch('/invoices', { drop: ['page'] }) });
    },
    onError: (err) => setActionError(err instanceof ApiError ? err.message : 'Could not delete'),
    onSettled: () => setConfirmDelete(false),
  });

  const download = useMutation({
    mutationFn: () => invoicesApi.pdf(id),
    onMutate: () => setActionError(null),
    onSuccess: ({ blob, filename }) =>
      triggerDownload(blob, filename ?? `invoice-${invoice?.invoiceNumber ?? id}.pdf`),
    onError: (err) =>
      setActionError(err instanceof ApiError ? err.message : 'Could not render the PDF'),
  });

  // Escape leaves the editor: back to wherever the user came from, or the list
  // as they left it. Goes through the router, so unsaved edits still get the
  // discard prompt above; off while a save, delete or send is in flight.
  useEscapeToGoBack({
    fallback: () => navigate({ to: '/invoices', search: lastSearch('/invoices') }),
    // Not while the Send dialog is open: Escape closes the dialog, not the page.
    enabled: !save.isPending && !remove.isPending && !sendOpen,
  });

  const sensors = useSensors(
    // A small threshold so a click into an input isn't hijacked into a drag.
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
  );

  /** Reordering IS an edit: `line_no` is the printed order, and the server
   *  renumbers densely from the array it receives. Marking the form dirty is
   *  what makes Save light up and the unsaved-changes guard arm. */
  /**
   * Forget which rows of the previous customer the current lines came from.
   *
   * Called when the customer changes while creating. The lines themselves stay
   * — description and cost are copies, and re-typing them would be hostile —
   * but every pointer here belongs to the customer that was selected a moment
   * ago: `sourceId` names a `customer_expenses` row, `engagementId` an
   * agreement they signed. Left in place they would answer "was this billed,
   * and under what" with the wrong customer's answer, and the server stores
   * both without checking whose they are.
   */
  const dropLineProvenance = () => {
    const current = form.getValues('lineItems');
    const carries = (l: LineValues) =>
      l.sourceType !== null || l.sourceId !== null || l.engagementId !== null;
    if (!current.some(carries)) return;
    form.setValue(
      'lineItems',
      current.map((l) => ({
        ...l,
        sourceType: null,
        sourceId: null,
        engagementId: null,
        sowVersion: null,
      })),
      { shouldDirty: true },
    );
  };

  const handleLineDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const from = lines.fields.findIndex((f) => f.id === active.id);
    const to = lines.fields.findIndex((f) => f.id === over.id);
    if (from === -1 || to === -1) return;
    lines.move(from, to);
    form.setValue('lineItems', form.getValues('lineItems'), { shouldDirty: true });
  };

  // Every field the preview and the totals read. `watch()` with no argument
  // re-renders on any change, which is exactly what a live preview wants.
  const values = form.watch();

  if (fallback) return fallback;

  if (!isNew && detail.isLoading) return <DetailSkeleton />;

  if (!isNew && (detail.isError || !invoice)) {
    return (
      <PageShell>
        <BackLink />
        <div className="rounded-lg border border-destructive/40 bg-destructive/5 p-6">
          <p className="text-destructive">
            {detail.error instanceof ApiError
              ? detail.error.message
              : 'Could not load the invoice.'}
          </p>
          {detail.error instanceof ApiError && detail.error.traceId ? (
            <p className="mt-1 text-muted-foreground text-xs">Trace ID: {detail.error.traceId}</p>
          ) : null}
          <Button size="sm" variant="outline" className="mt-3" onClick={() => detail.refetch()}>
            Retry
          </Button>
        </div>
      </PageShell>
    );
  }

  const snapshot: DocumentSnapshot | null = invoice
    ? {
        issuer: invoice.issuer,
        invoiceNumber: invoice.invoiceNumber,
        currency: invoice.currency,
        paymentDetails: invoice.paymentDetails,
      }
    : newDefaults && newBillTo
      ? {
          issuer: newDefaults.issuer,
          // Expected, not reserved — nothing is allocated until save, so two
          // people on this screen see the same number and the second to save
          // gets the next one.
          invoiceNumber: newDefaults.invoiceNumberPreview ?? '—',
          currency: newDefaults.currency,
          paymentDetails: newDefaults.paymentDetails,
        }
      : null;

  /** Which customer's expense catalog the line editor offers, and whose
   *  contacts the billing-contact picker lists. */
  const lineCustomerId = invoice?.customerId ?? customerId ?? '';

  const previewDocument = snapshot ? buildPreviewDocument(snapshot, values) : null;
  const totals = previewDocument ? computeInvoiceTotals(previewDocument) : null;
  const currency = snapshot?.currency ?? 'USD';

  /** The invoice-settings template filled in from what is on screen now —
   *  what the email says unless its message is edited. Null until there is a
   *  template and a snapshot to fill it from. */
  const emailBodyTemplate = invoice
    ? (detail.data?.related.emailBodyTemplate ?? null)
    : (newDefaults?.emailBodyTemplate ?? null);
  const emailSubjectTemplate = invoice
    ? (detail.data?.related.emailSubjectTemplate ?? null)
    : (newDefaults?.emailSubjectTemplate ?? null);
  const emailInvoiceNumber = isNew ? (snapshot?.invoiceNumber ?? '') : values.invoiceNumber;
  const emailValues = snapshot
    ? invoiceEmailValues({
        invoiceNumber: emailInvoiceNumber,
        total: totals?.balanceDue ?? '0',
        currency,
        dueDate: values.dueDate || null,
        servicePeriodStart: values.servicePeriodStart || null,
        servicePeriodEnd: values.servicePeriodEnd || null,
        customerName: values.billTo.name,
        issuerName: snapshot.issuer.name,
      })
    : null;
  const defaultEmailBody =
    emailValues && emailBodyTemplate !== null
      ? tryRenderInvoiceEmailBody(emailBodyTemplate, emailValues)
      : null;
  // The settings template filled in, exactly as the sender fills it.
  const emailSubject =
    emailValues && emailSubjectTemplate !== null
      ? tryRenderInvoiceEmailSubject(emailSubjectTemplate, emailValues)
      : null;

  const status = invoice?.status ?? null;
  const dirty = form.formState.isDirty;
  /** Save is shown only when there is something to save — and while it shows,
   *  it is THE next step, so the status action steps down to outline. */
  const showSave = sendEditable && (isNew || dirty || save.isPending);
  const canRenumber = editable && status === 'draft';
  const canUnapprove = canApprove && status === 'approved';
  const canDelete = canEdit && status === 'draft';

  const appendLine = () => {
    const accountId = defaultAccountFor('service');
    lines.append({
      kind: 'service',
      description: '',
      quantity: '',
      rate: '',
      amount: '',
      sourceType: null,
      sourceId: null,
      // A hand-added line belongs to no agreement until someone says so.
      engagementId: null,
      sowVersion: null,
      // The invoice-settings default for service lines, if there is one.
      allocations: accountId ? [{ accountId, amount: '0.00' }] : [],
    });
  };

  const focusField = (fieldId: string) => {
    const el = document.getElementById(fieldId);
    el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    el?.focus({ preventScroll: true });
  };

  const addEmail = () => {
    setAddingEmail(true);
    // The input mounts on the next render; scroll to and focus it then.
    requestAnimationFrame(() => focusField('invoice-bill-to-email'));
  };

  const edit = { shouldDirty: true, shouldValidate: true } as const;
  const changeInvoiceDate = (next: string) => {
    const prev = form.getValues('invoiceDate');
    const due = form.getValues('dueDate');
    form.setValue('invoiceDate', next, edit);
    // Keep the payment window that was agreed, whatever the terms label says:
    // a due date 38 days out stays 38 days out. Never read off the label.
    const gap = due === '' ? null : daysBetweenIso(prev, due);
    if (gap !== null && next !== '') form.setValue('dueDate', addIsoDays(next, gap), edit);
  };
  const pickTerms = (preset: TermsPreset) => {
    form.setValue('terms', preset.label, edit);
    const due = dueDateFor(form.getValues('invoiceDate'), preset.days);
    if (due !== '') form.setValue('dueDate', due, edit);
  };

  /** The To addresses on screen. */
  const toList = recipientAddresses(values.billTo.recipients, 'to');

  const blockers = snapshot
    ? invoiceBlockers({
        status,
        lineKinds: values.lineItems.map((l) => l.kind),
        unbookedLines: unbookedLineNumbers(values.lineItems),
        email: toList.join(', '),
        sendError: invoice?.sendError ?? null,
      })
    : [];
  // A send refusal the sender also recorded is already the "Last send failed"
  // row; say it once.
  const shownActionError =
    actionError !== null && actionError !== invoice?.sendError ? actionError : null;
  // Any error on the page blocks approval, not only the blockers: a field
  // still showing a message, or a refusal in the error row, means what would
  // be approved is not what the page says it is.
  const hasErrors = Object.keys(form.formState.errors).length > 0 || shownActionError !== null;
  const approvalBlocked = hasErrors || blockers.some((b) => b.blocks === 'approval');

  /** Not "expected" when nothing can be created: the reason (unconfigured
   *  settings, no customer access) is what belongs under the number. */
  let invoiceNumberHint: string | undefined;
  if (defaultsUnavailable) {
    invoiceNumberHint =
      defaults.error instanceof ApiError
        ? defaults.error.message
        : 'Could not load the expected number.';
  } else if (isNew) {
    invoiceNumberHint = 'Expected. Allocated when the invoice is created.';
  } else if (editable && !canRenumber) {
    invoiceNumberHint = 'Only a draft can be renumbered.';
  }

  /** Create mode's customer picker: in the Bill To cell until a customer is
   *  chosen, then inside the Bill To editor, where it can still be changed. */
  const customerPicker = (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor="new-invoice-customer">Customer</Label>
      <CustomerAutocomplete
        id="new-invoice-customer"
        value={customerId}
        onChange={(customer) => {
          const next = customer?.id ?? null;
          if (next !== customerId) {
            dropLineProvenance();
            // Delivery is on screen before a customer is, so addresses may
            // already have been typed with no customer to have come from.
            // Those survive the reset; addresses belonging to a customer
            // being switched AWAY from do not, which is the whole point of it.
            const typedRecipients =
              customerId === null && form.getFieldState('billTo.recipients').isDirty
                ? form.getValues('billTo.recipients')
                : null;
            // The Bill To block describes the customer selected a moment ago.
            // Clearing it back to defaults lets the seeding effect refill it
            // from the NEW customer — without this, one customer's address
            // would ride along on another's invoice.
            form.resetField('billTo');
            if (typedRecipients) {
              // Dirty, so the seeding effect leaves it alone rather than
              // overwriting it with the new customer's billing contact.
              form.setValue('billTo.recipients', typedRecipients, { shouldDirty: true });
            }
            // The expected number is tenant-level, but it moves whenever anyone
            // raises an invoice. Re-reading it on a customer SWITCH keeps it
            // from going stale on a screen left open a while; the first pick
            // follows the mount fetch too closely to learn anything new.
            if (customerId !== null && next !== null) {
              queryClient.invalidateQueries({
                queryKey: queryKeys.invoices.newDefaults(),
                exact: true,
              });
            }
          }
          setCustomerId(next);
        }}
        placeholder="Select customer…"
        className="w-full"
      />
    </div>
  );
  // Forced open while it holds an error, so the message has an input to sit
  // under rather than becoming a toast about a field nobody can see. The Save
  // handlers also latch `billToOpen`, so the editor does not snap shut under
  // the cursor on the first keystroke that clears the error.
  const billToErrors = form.formState.errors.billTo;
  const billToHasError = Boolean(
    billToErrors?.name || billToErrors?.addressLines || billToErrors?.contactId,
  );
  const billToExpanded = billToOpen || billToHasError;
  const selectedContact = (contacts.data?.data ?? []).find((c) => c.id === values.billTo.contactId);
  const billToDetail =
    [
      selectedContact ? billToContactName(selectedContact) : null,
      addressTextToLines(values.billTo.addressLines).slice(-1)[0],
    ]
      .filter(Boolean)
      .join(' · ') || 'No contact or address';

  return (
    <PageShell>
      {blockerDialog}
      {invoice && canSend && canSendInvoice(invoice.status) ? (
        <InvoiceSendDialog invoice={invoice} open={sendOpen} onOpenChange={setSendOpen} />
      ) : null}
      {invoice ? (
        <ConfirmDialog
          open={confirmDelete}
          title={`Delete invoice ${invoice.invoiceNumber}?`}
          description="The draft and its lines are deleted."
          confirmLabel="Delete"
          destructive
          confirmDisabled={remove.isPending}
          onConfirm={() => remove.mutate()}
          onCancel={() => setConfirmDelete(false)}
        />
      ) : null}
      {/* Unlocking drops a sign-off, so it is a deliberate, confirmed step —
          never a side effect of editing. */}
      {invoice ? (
        <ConfirmDialog
          open={confirmUnlock}
          title={`Unlock invoice ${invoice.invoiceNumber}?`}
          description="It goes back to draft so it can be edited. Its approval is cleared, so it will need approving again."
          confirmLabel="Unlock"
          confirmDisabled={transition.isPending}
          onConfirm={() =>
            transition.mutate('unapprove', { onSettled: () => setConfirmUnlock(false) })
          }
          onCancel={() => setConfirmUnlock(false)}
        />
      ) : null}
      <BackLink />

      <div className="flex flex-wrap items-center justify-between gap-3">
        {/* The number is the heading, and it follows the Invoice number field
            as it is typed. The field itself lives with the other details. */}
        <div className="flex items-center gap-3">
          <h1 className="text-section-title num-code">
            {isNew ? 'New invoice' : values.invoiceNumber.trim() || invoice?.invoiceNumber}
          </h1>
          {invoice ? <InvoiceStatusBadge status={invoice.status} /> : null}
          {invoice ? <InvoiceTypeBadge type={invoice.invoiceType} /> : null}
          {/* What became of the email — the last meaningful status, with the
              sent / delivered / opened details on hover, click or tap. */}
          {invoice ? <InvoiceEmailBadge invoice={invoice} /> : null}
        </div>

        {/* One filled button: Save while there are edits, otherwise the
            status's next step — Approve, then Send, then the PDF once sent.
            Everything rarer lives in the ⋯ menu. */}
        <div className="flex items-center gap-2">
          {showSave ? (
            <Button
              onClick={form.handleSubmit(
                (v) => save.mutate(v),
                (errs) => {
                  // Keep the Invoice to editor open until its fields are fixed.
                  if (errs.billTo?.name || errs.billTo?.addressLines || errs.billTo?.contactId) {
                    setBillToOpen(true);
                  }
                  setActionError(reportInvalidSubmit(errs));
                },
              )}
              // Create needs a customer before there is anything to raise.
              disabled={save.isPending || (isNew && customerId === null)}
            >
              {save.isPending ? <Loader2 className="animate-spin" /> : null}
              {save.isPending
                ? isNew
                  ? 'Creating…'
                  : 'Saving…'
                : isNew
                  ? 'Create invoice'
                  : 'Save'}
            </Button>
          ) : null}
          {/* Approves the SAVED invoice, so unsaved edits disable it rather than
              approving something other than what is on screen. */}
          {!isNew && canApprove && status === 'draft' ? (
            <Button
              variant={showSave ? 'outline' : 'default'}
              onClick={() => transition.mutate('approve')}
              // The strip under the header already says what blocks approval
              // and how to fix it; a click the server can only refuse adds
              // nothing but a second copy of that message.
              disabled={transition.isPending || dirty || approvalBlocked}
              title={
                dirty
                  ? 'Save your changes first'
                  : hasErrors
                    ? 'Fix the errors on this page first'
                    : approvalBlocked
                      ? 'Fix what’s blocking approval first'
                      : undefined
              }
            >
              Approve
            </Button>
          ) : null}
          {/* Sends the SAVED invoice, so unsaved edits disable it rather than
              being silently left out of what the customer receives. A sent
              invoice can be sent again; Download stays its primary action. */}
          {!isNew && canSend && canSendInvoice(status) ? (
            <Button
              variant={showSave || status === 'sent' ? 'outline' : 'default'}
              onClick={() => setSendOpen(true)}
              disabled={dirty}
              title={dirty ? 'Save your changes first' : 'Email the invoice now'}
            >
              <Send className="size-4" />
              {status === 'sent' ? 'Resend' : 'Send'}
            </Button>
          ) : null}
          {isNew ? null : status === 'sent' ? (
            <Button onClick={() => download.mutate()} disabled={download.isPending}>
              {download.isPending ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <Download className="size-4" />
              )}
              {download.isPending ? 'Rendering…' : 'Download PDF'}
            </Button>
          ) : (
            <Button
              variant="outline"
              size="icon"
              onClick={() => download.mutate()}
              disabled={download.isPending}
              aria-label="Download PDF"
              title="Download the PDF"
            >
              {download.isPending ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <Download className="size-4" />
              )}
            </Button>
          )}
          {canUnapprove || canDelete ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" aria-label="More actions">
                  <MoreHorizontal className="size-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {canUnapprove ? (
                  <DropdownMenuItem
                    disabled={transition.isPending}
                    onSelect={() => setConfirmUnlock(true)}
                  >
                    <Undo2 className="size-4" />
                    Unlock
                  </DropdownMenuItem>
                ) : null}
                {canDelete ? (
                  <DropdownMenuItem
                    className="text-destructive focus:text-destructive"
                    onSelect={() => setConfirmDelete(true)}
                  >
                    <Trash2 className="size-4 text-destructive" />
                    Delete invoice
                  </DropdownMenuItem>
                ) : null}
              </DropdownMenuContent>
            </DropdownMenu>
          ) : null}
        </div>
      </div>

      {/* Approval locks the content (the server refuses an edit), so say so
          where the disabled fields would otherwise read as a bug — and offer
          the one way to change it. */}
      {status === 'approved' ? (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border bg-muted/40 px-4 py-2.5 text-sm">
          <span>Approved and locked. Only when to send can still change.</span>
          {canUnapprove ? (
            <button
              type="button"
              onClick={() => setConfirmUnlock(true)}
              disabled={transition.isPending || dirty}
              title={dirty ? 'Save your changes first' : undefined}
              className="ml-auto font-medium underline underline-offset-2 hover:no-underline disabled:opacity-50"
            >
              Unlock
            </button>
          ) : null}
        </div>
      ) : null}

      {status === 'sent' ? (
        <div className="flex items-center gap-2 rounded-lg border bg-muted/40 px-4 py-2.5 text-sm">
          <Lock className="size-4 shrink-0 text-muted-foreground" aria-hidden />
          <span>
            {invoice?.sentAt
              ? `Sent to the customer on ${formatInstantDate(invoice.sentAt)} and locked.`
              : 'Sent to the customer and locked.'}{' '}
            It can no longer be changed.
          </span>
        </div>
      ) : null}

      <InvoiceAttention
        blockers={blockers}
        flags={detail.data?.related.flags ?? []}
        priorInvoiceId={invoice?.priorInvoiceId ?? null}
        error={shownActionError}
        onDismissError={() => setActionError(null)}
        onAddLine={editable ? appendLine : undefined}
        onAddEmail={editable ? addEmail : undefined}
      />

      {/* Same card-wrapped pill tabs as the customer and employee detail
          pages, so every record page reads as one product. */}
      <PillTabs
        value={view}
        onValueChange={(v) => setView(v === 'preview' ? 'preview' : 'edit')}
        className="gap-4 rounded-xl border bg-card p-4"
      >
        <PillTabsList fullWidth>
          <PillTabsTrigger value="edit">
            <Pencil />
            Edit
          </PillTabsTrigger>
          <PillTabsTrigger value="preview">
            <Eye />
            Preview PDF
          </PillTabsTrigger>
        </PillTabsList>

        {/* Kept mounted while the preview shows: the inputs are what
            `reportInvalidSubmit` scrolls to, and a Save pressed from the
            preview must still find them. */}
        <PillTabsContent value="edit" forceMount className="data-[state=inactive]:hidden">
          <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_22rem]">
            <div className="flex min-w-0 flex-col gap-6">
              {/* The facts that decide who owes what, and when, in one strip —
                  in the order the printed header reads them. */}
              <section aria-label="Invoice details" className="rounded-lg border">
                <div className="grid divide-y md:grid-cols-[minmax(0,1.4fr)_repeat(2,minmax(0,1fr))] md:divide-x md:divide-y-0">
                  <div className="flex min-w-0 flex-col gap-1.5 p-3">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-medium text-sm">Invoice to</span>
                      {snapshot && editable ? (
                        <Button
                          type="button"
                          variant="link"
                          size="sm"
                          className="h-auto p-0 text-xs"
                          aria-expanded={billToExpanded}
                          aria-controls="invoice-bill-to-editor"
                          onClick={() => setBillToOpen((v) => (billToHasError ? true : !v))}
                        >
                          {billToExpanded ? 'Done' : 'Change'}
                        </Button>
                      ) : null}
                    </div>
                    {isNew && customerId === null ? (
                      customerPicker
                    ) : isNew && (defaults.isPending || billToDefaults.isPending) ? (
                      <p className="flex items-center gap-2 text-muted-foreground text-sm">
                        <Loader2 className="size-4 animate-spin" />
                        Loading the invoicing details…
                      </p>
                    ) : defaultsUnavailable || (isNew && billToDefaults.isError && !newBillTo) ? (
                      // Either half: the cell needs both to draw anything, and
                      // the not-configured 400 comes from the tenant half. Only
                      // with no data: a failed background refetch keeps the last
                      // good copy, and must not tear down a cell being edited.
                      <DefaultsError
                        error={defaultsUnavailable ? defaults.error : billToDefaults.error}
                        fallback="Could not load the invoicing details."
                      />
                    ) : snapshot && billToExpanded ? (
                      // Edited IN the cell it summarises — the strip row grows
                      // rather than the editor opening somewhere else. It edits
                      // THIS INVOICE only: the snapshot columns, never the
                      // customer record. Forced open while it holds an error,
                      // so the message has an input to sit under.
                      <div id="invoice-bill-to-editor" className="flex flex-col gap-4 pt-1">
                        <p className="text-muted-foreground text-xs">
                          Printed on the invoice. Edits change this invoice only, not the customer.
                        </p>
                        {isNew ? customerPicker : null}
                        <BillToFields
                          form={form}
                          customerId={billToCustomerId}
                          contacts={contacts.data?.data ?? []}
                          contactsLoading={contacts.isPending}
                          contactsError={contacts.isError}
                          onRetryContacts={() => contacts.refetch()}
                          billingAddress={
                            invoice
                              ? detail.data?.related.billingAddress
                              : newBillTo?.billingAddress
                          }
                          editable={editable}
                          canAddContact={canAddContact}
                        />
                      </div>
                    ) : snapshot && editable ? (
                      // The summary itself is the way in, not just the small
                      // Change link beside it.
                      <button
                        type="button"
                        onClick={() => setBillToOpen(true)}
                        aria-expanded={false}
                        aria-controls="invoice-bill-to-editor"
                        title="Edit the bill-to details"
                        className="-mx-1.5 min-w-0 rounded-md px-1.5 py-1 text-left outline-none transition-colors hover:bg-muted focus-visible:ring-[3px] focus-visible:ring-ring/50"
                      >
                        <span className="block truncate font-medium text-sm">
                          {values.billTo.name || '—'}
                        </span>
                        <span className="block truncate text-muted-foreground text-xs">
                          {billToDetail}
                        </span>
                      </button>
                    ) : snapshot ? (
                      <div className="min-w-0">
                        <p className="truncate font-medium text-sm" title={values.billTo.name}>
                          {values.billTo.name || '—'}
                        </p>
                        <p className="truncate text-muted-foreground text-xs">{billToDetail}</p>
                      </div>
                    ) : null}
                  </div>
                  {/* Drafts only: an approved invoice was signed off under its
                      number. Duplicates are refused by the server on save. On
                      create nothing is allocated until save, so the number
                      shown is only what it is expected to be — see the
                      preview note on `newInvoiceDefaults`. */}
                  <Field
                    label="Invoice number"
                    className="p-3"
                    htmlFor="invoice-number"
                    error={form.formState.errors.invoiceNumber?.message}
                    hint={invoiceNumberHint}
                  >
                    {isNew ? (
                      <Input
                        id="invoice-number"
                        className="num-code"
                        value={newDefaults?.invoiceNumberPreview ?? ''}
                        placeholder={defaultsUnavailable ? 'Unavailable' : 'Allocated on save'}
                        readOnly
                        disabled
                      />
                    ) : (
                      <Input
                        id="invoice-number"
                        className="num-code"
                        autoComplete="off"
                        spellCheck={false}
                        disabled={!canRenumber}
                        aria-invalid={Boolean(form.formState.errors.invoiceNumber)}
                        {...form.register('invoiceNumber')}
                      />
                    )}
                  </Field>
                  <Field
                    label="Invoice date"
                    className="p-3"
                    error={form.formState.errors.invoiceDate?.message}
                  >
                    <DatePicker
                      value={values.invoiceDate}
                      onChange={changeInvoiceDate}
                      disabled={!editable}
                      ariaInvalid={Boolean(form.formState.errors.invoiceDate)}
                    />
                  </Field>
                </div>
                {/* Terms beside the due date they set, then the service
                    period — the order the printed header reads them. The period
                    is a real date range, not prose: it is what a timesheet
                    entry or a customer expense gets compared against. */}
                <div className="grid gap-4 border-t p-3 sm:grid-cols-2 lg:grid-cols-4">
                  <Field
                    label="Terms"
                    htmlFor="invoice-terms"
                    error={form.formState.errors.terms?.message}
                  >
                    <InvoiceTermsInput
                      id="invoice-terms"
                      invoiceDate={values.invoiceDate}
                      disabled={!editable}
                      invalid={Boolean(form.formState.errors.terms)}
                      onPick={pickTerms}
                      inputProps={form.register('terms')}
                    />
                  </Field>
                  <Field label="Due date" error={form.formState.errors.dueDate?.message}>
                    <DatePicker
                      value={values.dueDate}
                      onChange={(v) => form.setValue('dueDate', v, edit)}
                      disabled={!editable}
                      clearable
                      placeholder="No due date"
                      ariaInvalid={Boolean(form.formState.errors.dueDate)}
                    />
                  </Field>
                  <Field
                    label="Service period start"
                    error={form.formState.errors.servicePeriodStart?.message}
                  >
                    <DatePicker
                      value={values.servicePeriodStart}
                      onChange={(v) => form.setValue('servicePeriodStart', v, edit)}
                      disabled={!editable}
                      clearable
                      placeholder="No period"
                    />
                  </Field>
                  {/* Shown INCLUSIVE: September reads as ending Sep 30, not
                      Oct 1. The column stays half-open `[start, end)`; the
                      conversion happens here, at the edge. */}
                  <Field
                    label="Service period end"
                    error={form.formState.errors.servicePeriodEnd?.message}
                  >
                    <DatePicker
                      value={servicePeriodEndToInclusive(values.servicePeriodEnd)}
                      onChange={(v) =>
                        form.setValue('servicePeriodEnd', servicePeriodEndFromInclusive(v), edit)
                      }
                      disabled={!editable}
                      clearable
                      placeholder="No period"
                    />
                  </Field>
                </div>
              </section>

              <section className="rounded-lg border p-4">
                <div className="mb-3 flex items-center justify-between">
                  <h2 className="font-medium text-sm">Line items</h2>
                  {editable ? (
                    <Button size="sm" variant="outline" onClick={appendLine}>
                      <Plus className="size-4" />
                      Add line
                    </Button>
                  ) : null}
                </div>

                {lines.fields.length === 0 ? (
                  <p className="py-6 text-center text-muted-foreground text-sm">No lines yet.</p>
                ) : (
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    modifiers={[restrictToVerticalAxis, restrictToParentElement]}
                    onDragEnd={handleLineDragEnd}
                  >
                    <SortableContext
                      items={lines.fields.map((f) => f.id)}
                      strategy={verticalListSortingStrategy}
                    >
                      <LineItemTable
                        fields={lines.fields}
                        form={form}
                        editable={editable}
                        customerId={lineCustomerId}
                        asOf={values.invoiceDate}
                        onRemove={(i) => lines.remove(i)}
                      />
                    </SortableContext>
                  </DndContext>
                )}

                {totals ? (
                  <dl className="mt-4 ml-auto flex w-full max-w-xs flex-col gap-1 border-t pt-3 text-sm">
                    <Total label="Total" value={formatCurrency(totals.total, currency)} />
                    <Total
                      label="Payments/Credits"
                      value={formatCurrency(totals.paymentsCredits, currency)}
                    />
                    <Total
                      label="Balance due"
                      value={formatCurrency(totals.balanceDue, currency)}
                      emphasis
                    />
                  </dl>
                ) : null}
              </section>

              <section className="rounded-lg border p-4">
                <Field
                  label="Notes"
                  htmlFor="invoice-notes"
                  hint={NOTES_HINT}
                  error={form.formState.errors.notes?.message}
                >
                  <Textarea
                    id="invoice-notes"
                    rows={3}
                    disabled={!editable}
                    {...form.register('notes')}
                  />
                </Field>
              </section>
            </div>

            {/* Everything about getting this invoice out: what stands in the
                way, who it goes to, and what goes with it. */}
            <aside aria-label="Approval and delivery" className="flex min-w-0 flex-col gap-4">
              {/* Present from the first paint, including before a customer is
                  picked: none of these fields come from the invoice's snapshot
                  columns, and a create screen whose right-hand column is empty
                  reads as though a raised invoice has nowhere to go. To and CC
                  fill in from the billing contact once a customer is chosen. */}
              <section className="rounded-lg border p-4">
                <SectionHeading title="Delivery" />
                <DeliveryFields
                  form={form}
                  editable={editable}
                  sendEditable={sendEditable}
                  status={status}
                  addingEmail={addingEmail}
                  onAddingEmailChange={setAddingEmail}
                  defaultEmailBody={defaultEmailBody}
                  emailSubject={emailSubject}
                />
              </section>
              {/* Not part of the form: each file is its own request, so
                  attaching one never needs Save and never un-approves the
                  invoice. It needs an invoice to attach to. */}
              {isNew ? (
                <section className="rounded-lg border p-4">
                  <h2 className="mb-1 font-medium text-sm">Attachments</h2>
                  <p className="text-muted-foreground text-sm">
                    Create the invoice first, then add attachments.
                  </p>
                </section>
              ) : (
                <InvoiceAttachments
                  invoiceId={id}
                  editable={editable}
                  // Only a real lock gets a note: a draft this viewer may not
                  // edit is closed to them, but not locked.
                  lockedBy={status === 'sent' ? 'sent' : status === 'approved' ? 'approved' : null}
                />
              )}
            </aside>
          </div>
        </PillTabsContent>

        <PillTabsContent value="preview">
          <div className="mx-auto max-w-4xl rounded-lg border bg-neutral-100 p-6">
            {previewDocument ? (
              <InvoiceDocumentPreview document={previewDocument} />
            ) : isNew && customerId !== null && (defaults.isPending || billToDefaults.isPending) ? (
              // Distinct from "no customer yet": the document is on its way,
              // and collapsing the two would read as nothing having happened.
              <p className="flex items-center justify-center gap-2 p-8 text-muted-foreground text-sm">
                <Loader2 className="size-4 animate-spin" />
                Building the preview…
              </p>
            ) : isNew && (defaults.isError || billToDefaults.isError) ? (
              <div className="p-8 text-center">
                <DefaultsError
                  error={defaults.error ?? billToDefaults.error}
                  fallback="Could not build the preview."
                />
              </div>
            ) : (
              <p className="p-8 text-center text-muted-foreground text-sm">
                Pick a customer to preview the document.
              </p>
            )}
          </div>
        </PillTabsContent>
      </PillTabs>
    </PageShell>
  );
}

/**
 * The per-line behaviour both layouts share: which inputs apply to this kind,
 * the derived amount, and what picking a customer expense does.
 *
 * Extracted so the card and the table cannot drift — two copies of "a discount
 * has no rate" is exactly the kind of duplication that ends with one layout
 * printing a Qty the other hides.
 */
function useLineItem(form: UseFormReturn<FormValues>, index: number) {
  const defaultAccountFor = useDefaultAccountForKind();
  const allocations = form.watch(`lineItems.${index}.allocations`) ?? [];
  const amount = form.watch(`lineItems.${index}.amount`);

  /**
   * The split as it stood before the line amount started changing. Rescaling
   * every keystroke from the previous keystroke's rounded parts would let the
   * proportions drift; rescaling from this keeps them exact. Cleared by any
   * edit to the accounts themselves.
   */
  const splitBasis = useRef<LineValues['allocations'] | null>(null);

  const writeAllocations = (next: LineValues['allocations']) =>
    form.setValue(`lineItems.${index}.allocations`, next, {
      shouldDirty: true,
      // Re-check only when a row is showing an error, so picking its account
      // clears the message on the spot.
      shouldValidate: form.getFieldState(`lineItems.${index}.allocations`).error !== undefined,
    });

  /** Replace this line's accounts (an edit to the accounts themselves). */
  const setAllocations = (next: LineValues['allocations']) => {
    splitBasis.current = null;
    writeAllocations(next);
  };

  /** Follow a new line amount: one account takes it whole, a split keeps its
   *  proportions to the cent. */
  const followAmount = (next: string) => {
    const current = form.getValues(`lineItems.${index}.allocations`) ?? [];
    if (current.length === 0) return;
    splitBasis.current ??= current;
    writeAllocations(rescaleAllocations(splitBasis.current, next));
  };

  /** Give an unassigned line its kind's default account, if there is one. */
  const applyDefaultAccount = (k: InvoiceLineKind) => {
    const current = form.getValues(`lineItems.${index}.allocations`) ?? [];
    if (current.length > 0) return;
    const accountId = defaultAccountFor(k);
    if (accountId) {
      setAllocations([
        { accountId, amount: form.getValues(`lineItems.${index}.amount`) || '0.00' },
      ]);
    }
  };

  const kind = form.watch(`lineItems.${index}.kind`);
  const quantity = form.watch(`lineItems.${index}.quantity`);
  const rate = form.watch(`lineItems.${index}.rate`);
  const showQtyRate = hasQuantityAndRate(kind);

  // With both a quantity and a rate, the amount IS their product — so it is
  // computed and locked rather than typed. Leaving it editable would allow a
  // row that prints "3.5 × 195.00 = 500.00", which no reader can reconcile.
  // A flat-fee row (no quantity) still takes an amount directly, which is how
  // retainers and one-off charges are expressed.
  const derived = showQtyRate ? derivedAmount(quantity, rate) : null;

  /**
   * Recompute the amount from an EDIT to quantity or rate.
   *
   * Deliberately not an effect on `derived`. An effect fires on seed too, and
   * a line may legitimately store an amount that differs from quantity × rate
   * — the API preserves an explicit amount untouched, which is what makes a
   * negotiated round-up or a rounding adjustment expressible. Deriving on
   * mount rewrote exactly those, marked the form dirty before the user had
   * touched anything, and armed the unsaved-changes guard over an edit nobody
   * made. Recomputing only when the operator changes an input means the
   * override survives being looked at, and is superseded the moment they ask
   * for it to be.
   *
   * Takes the new value explicitly rather than re-reading form state, because
   * react-hook-form's own `onChange` has not necessarily committed it yet.
   */
  const recompute = (next: { quantity?: string; rate?: string }) => {
    const q = next.quantity ?? form.getValues(`lineItems.${index}.quantity`);
    const r = next.rate ?? form.getValues(`lineItems.${index}.rate`);
    const amount = derivedAmount(q, r);
    if (amount === null) return;
    const path = `lineItems.${index}.amount` as const;
    // Re-validate only when the amount is showing an error (a "Required" from
    // the last Save), so the computed value clears it on the spot instead of
    // leaving a stale message under a filled-in, read-only field.
    form.setValue(path, amount, {
      shouldDirty: true,
      shouldValidate: form.getFieldState(path).error !== undefined,
    });
    followAmount(amount);
  };

  /** The Amount input, typed directly (flat-fee and discount lines). */
  const amountField = form.register(`lineItems.${index}.amount`);
  const onAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    amountField.onChange(e);
    followAmount(e.target.value);
  };

  /** Registered once here so BOTH layouts bind the same handler — the card and
   *  the table must not drift on when an amount is recomputed. */
  const quantityField = form.register(`lineItems.${index}.quantity`);
  const rateField = form.register(`lineItems.${index}.rate`);
  const onQuantityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    quantityField.onChange(e);
    recompute({ quantity: e.target.value });
  };
  const onRateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    rateField.onChange(e);
    recompute({ rate: e.target.value });
  };

  const setKind = (v: string) => {
    const next = v as InvoiceLineKind;
    form.setValue(`lineItems.${index}.kind`, next, { shouldDirty: true });
    // Switching TO a discount hides the qty/rate inputs, and `toPatch` drops
    // whatever they held — otherwise the stored row would print a Qty the
    // form no longer shows.
    if (!hasQuantityAndRate(next)) {
      form.setValue(`lineItems.${index}.quantity`, '', { shouldDirty: true });
      form.setValue(`lineItems.${index}.rate`, '', { shouldDirty: true });
    }
    applyDefaultAccount(next);
  };

  const applyExpense = (expense: CustomerExpense) => {
    // Copy the description and cost onto the line; keep the pointer back. The
    // customer's expense record may be re-priced later and this invoice must
    // keep saying what it charged.
    form.setValue(`lineItems.${index}.description`, expense.description, { shouldDirty: true });
    // A variable expense has no standing amount to copy — the whole point is
    // that it differs each time. Leave the field empty for the user to fill:
    // this manual path is exactly how such an expense gets billed, which is
    // why the picker still offers it even though generation skips it.
    form.setValue(`lineItems.${index}.amount`, expense.cost ?? '', { shouldDirty: true });
    form.setValue(`lineItems.${index}.quantity`, '', { shouldDirty: true });
    form.setValue(`lineItems.${index}.rate`, '', { shouldDirty: true });
    form.setValue(`lineItems.${index}.sourceType`, CUSTOMER_EXPENSE_SOURCE, { shouldDirty: true });
    form.setValue(`lineItems.${index}.sourceId`, expense.id, { shouldDirty: true });
    followAmount(expense.cost ?? '');
    applyDefaultAccount('expense');
  };

  return {
    kind,
    showQtyRate,
    derived,
    setKind,
    applyExpense,
    quantityField,
    rateField,
    onQuantityChange,
    onRateChange,
    amountField,
    onAmountChange,
    amount,
    allocations,
    setAllocations,
  };
}

/**
 * The QuickBooks-shaped line-item grid: a numbered row per line, one column per
 * field, drag handle on the left and delete on the right.
 *
 * Only rendered when the preview pane is collapsed. Seven columns need the
 * width — squeezed into half a screen the description cell becomes unusable,
 * which is why {@link LineItemCard} exists for the split view. Both bind to the
 * same field array and both are sortable, so which one is on screen changes the
 * shape and nothing else.
 */
function LineItemTable({
  fields,
  form,
  editable,
  customerId,
  asOf,
  onRemove,
}: {
  fields: Array<{ id: string }>;
  form: UseFormReturn<FormValues>;
  editable: boolean;
  customerId: string;
  asOf: string;
  onRemove: (index: number) => void;
}) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="border-b bg-muted/50 text-muted-foreground text-xs">
            {/* Two unlabelled utility columns, as QuickBooks has: the drag
                handle and the row number. */}
            <th className="w-8" />
            <th className="w-8 py-2 text-right font-medium">#</th>
            <th className="w-32 px-2 py-2 text-left font-medium">Type</th>
            <th className="min-w-48 px-2 py-2 text-left font-medium">Description</th>
            <th className="w-20 px-2 py-2 text-right font-medium">Qty</th>
            <th className="w-28 px-2 py-2 text-right font-medium">Rate</th>
            <th className="w-28 px-2 py-2 text-right font-medium">Amount</th>
            {/* Finance-only: where each line's money is booked. Never printed. */}
            <th className="w-52 px-2 py-2 text-left font-medium">Account</th>
            <th className="w-20" />
          </tr>
        </thead>
        <tbody>
          {fields.map((field, i) => (
            <LineItemTableRow
              key={field.id}
              id={field.id}
              form={form}
              index={i}
              editable={editable}
              customerId={customerId}
              asOf={asOf}
              onRemove={() => onRemove(i)}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function LineItemTableRow({
  id,
  form,
  index,
  editable,
  customerId,
  asOf,
  onRemove,
}: {
  id: string;
  form: UseFormReturn<FormValues>;
  index: number;
  editable: boolean;
  customerId: string;
  asOf: string;
  onRemove: () => void;
}) {
  const sortable = useSortable({ id, disabled: !editable });
  const line = useLineItem(form, index);
  const errors = form.formState.errors.lineItems?.[index];

  return (
    <>
      <tr
        ref={sortable.setNodeRef}
        style={{
          transform: CSS.Transform.toString(sortable.transform),
          transition: sortable.transition,
        }}
        className={`border-b align-top ${sortable.isDragging ? 'bg-background shadow-lg' : ''}`}
      >
        {/* The row is top-aligned so a line's inputs stay level when an
            expense line grows a picker under its description; the handle and
            the number sit centred on the row instead. */}
        <td className="py-1 align-middle">
          {editable ? <DragHandle sortable={sortable} label={`Reorder line ${index + 1}`} /> : null}
        </td>
        <td className="py-1 pr-1 text-right align-middle text-muted-foreground text-xs tabular-nums">
          {index + 1}
        </td>
        <td className="px-1 py-1">
          <Select value={line.kind} onValueChange={line.setKind} disabled={!editable}>
            <SelectTrigger aria-label={`Line ${index + 1} kind`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {pickableLineKinds(line.kind).map((k) => (
                <SelectItem key={k} value={k}>
                  {INVOICE_LINE_KIND_LABELS[k]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </td>
        <td className="px-1 py-1">
          <Textarea
            rows={1}
            placeholder="Description"
            disabled={!editable}
            aria-label={`Line ${index + 1} description`}
            aria-invalid={Boolean(errors?.description)}
            // The shared Textarea uses `field-sizing-content`, so each line
            // starts at input height and grows vertically as its description
            // wraps instead of forcing the operator to scroll sideways.
            className="min-h-9 resize-none overflow-hidden py-2"
            {...form.register(`lineItems.${index}.description`)}
          />
          <CellError message={errors?.description?.message} />
          {line.kind === 'expense' && editable ? (
            <div className="mt-1">
              <CustomerExpensePicker
                customerId={customerId}
                asOf={asOf}
                onPick={line.applyExpense}
                aria-label={`Line ${index + 1} expense`}
              />
            </div>
          ) : null}
        </td>
        {/* A discount is one signed figure — the Qty and Rate cells stay
            structurally present so the grid does not reflow, but hold nothing
            to type into. */}
        <td className="px-1 py-1">
          {line.showQtyRate ? (
            <Input
              placeholder="—"
              inputMode="decimal"
              disabled={!editable}
              aria-label={`Line ${index + 1} quantity`}
              aria-invalid={Boolean(errors?.quantity)}
              className="text-right tabular-nums"
              {...line.quantityField}
              onChange={line.onQuantityChange}
            />
          ) : null}
          {line.showQtyRate ? <CellError message={errors?.quantity?.message} /> : null}
        </td>
        <td className="px-1 py-1">
          {line.showQtyRate ? (
            <Input
              placeholder="—"
              inputMode="decimal"
              disabled={!editable}
              aria-label={`Line ${index + 1} rate`}
              aria-invalid={Boolean(errors?.rate)}
              className="text-right tabular-nums"
              {...line.rateField}
              onChange={line.onRateChange}
            />
          ) : null}
          {line.showQtyRate ? <CellError message={errors?.rate?.message} /> : null}
        </td>
        <td className="px-1 py-1">
          <Input
            placeholder="0.00"
            inputMode="decimal"
            readOnly={line.derived !== null}
            disabled={!editable}
            aria-label={`Line ${index + 1} amount`}
            aria-invalid={Boolean(errors?.amount)}
            title={line.derived !== null ? 'Computed from quantity × rate' : undefined}
            className={`text-right tabular-nums ${
              line.derived !== null ? 'bg-muted text-muted-foreground' : ''
            }`}
            {...line.amountField}
            onChange={line.onAmountChange}
          />
          <CellError message={errors?.amount?.message} />
        </td>
        <td className="px-1 py-1">
          <LineAccountCell
            lineNo={index + 1}
            allocations={line.allocations}
            amount={line.amount}
            editable={editable}
            onChange={line.setAllocations}
          />
        </td>
        <td className="whitespace-nowrap py-1 text-right">
          {editable ? (
            <>
              <Button
                size="icon"
                variant="ghost"
                aria-label={
                  line.allocations.length >= 2
                    ? `Add another account to line ${index + 1}`
                    : `Split line ${index + 1} across accounts`
                }
                aria-pressed={line.allocations.length >= 2}
                title={
                  line.allocations.length >= 2
                    ? 'Add another account to the split'
                    : 'Split across accounts'
                }
                className={line.allocations.length >= 2 ? 'bg-primary/10 text-primary' : undefined}
                onClick={() => line.setAllocations(addSplitRow(line.allocations, line.amount))}
              >
                <Split className="size-4" />
              </Button>
              <Button
                size="icon"
                variant="ghost"
                aria-label={`Remove line ${index + 1}`}
                onClick={onRemove}
              >
                <Trash2 className="size-4" />
              </Button>
            </>
          ) : null}
        </td>
      </tr>
      {line.allocations.length >= 2 ? (
        <LineSplitRows
          lineNo={index + 1}
          allocations={line.allocations}
          amount={line.amount}
          editable={editable}
          // drag, #, type, description, qty, rate
          leadingColumns={6}
          errors={
            errors?.allocations as
              | ({ accountId?: { message?: string }; amount?: { message?: string } } | undefined)[]
              | undefined
          }
          onChange={line.setAllocations}
        />
      ) : null}
      {/* Each input carries its own message; this row is only for what has
          no cell to sit in (e.g. a Qty left over on a kind that hides it). */}
      {hasLineErrors(errors, line.showQtyRate) ? (
        <tr>
          <td colSpan={9} className="pb-2 pl-16">
            <LineErrors errors={errors} showQtyRate={line.showQtyRate} />
          </td>
        </tr>
      ) : null}
    </>
  );
}

/** The grab affordance. `listeners`/`attributes` must land on the handle, not
 *  the row: with them on the row, every click into a text input starts a drag
 *  and the inputs become untypeable. */
function DragHandle({
  sortable,
  label,
}: {
  sortable: ReturnType<typeof useSortable>;
  label: string;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      className="cursor-grab touch-none text-muted-foreground hover:text-foreground active:cursor-grabbing"
      {...sortable.attributes}
      {...sortable.listeners}
    >
      <GripVertical />
    </button>
  );
}

function BackLink() {
  return (
    <Link
      to="/invoices"
      search={lastSearch('/invoices')}
      className="inline-flex w-fit items-center gap-1 text-muted-foreground text-sm hover:text-foreground"
    >
      <ArrowLeft />
      Invoices
    </Link>
  );
}

/**
 * The `new-defaults` failure, with a way out of it.
 *
 * One of these errors is not really an error: a tenant that has never saved
 * invoice settings gets a field-scoped 400 on `invoiceSettings`, and the fix
 * is a screen, not a retry. Detecting it on the field path rather than by
 * matching the message keeps the copy free to change without silently
 * dropping the link (G15 — a data gap must not look like normal output).
 */
function DefaultsError({ error, fallback }: { error: unknown; fallback: string }) {
  const notConfigured =
    error instanceof ApiError && error.fields?.some((f) => f.path === 'invoiceSettings');
  return (
    <div className="space-y-1.5">
      <p className="text-destructive text-sm">
        {error instanceof ApiError ? error.message : fallback}
      </p>
      {notConfigured ? (
        <Link
          to="/invoices/settings"
          className="inline-flex items-center gap-1 text-sm underline underline-offset-2"
        >
          Go to Invoice settings
          <ArrowRight className="size-3" />
        </Link>
      ) : null}
    </div>
  );
}

/** A section's title, with one line that says what the whole section is for —
 *  in place of a hint under every field in it. */
function SectionHeading({ title, caption }: { title: string; caption?: string }) {
  return (
    <div className="mb-3 flex flex-col gap-0.5">
      <h2 className="font-medium text-sm">{title}</h2>
      {caption ? <p className="text-muted-foreground text-xs">{caption}</p> : null}
    </div>
  );
}

function Field({
  label,
  error,
  hint,
  htmlFor,
  className,
  children,
}: {
  label: string;
  error?: string;
  hint?: string;
  htmlFor?: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <Label htmlFor={htmlFor}>{label}</Label>
      {children}
      {hint ? <p className="text-muted-foreground text-xs">{hint}</p> : null}
      {error ? <p className="text-destructive text-xs">{error}</p> : null}
    </div>
  );
}

/** How a contact reads in the picker. Preferred name wins over the legal
 *  first name, matching `billToContactName` — the same string ends up as the
 *  first line of the printed address block. */
function contactLabel(contact: CustomerContact): string {
  const who = billToContactName(contact) ?? 'Unnamed contact';
  return contact.email ? `${who} — ${contact.email}` : who;
}

/**
 * Stands in for a contact the invoice still points at but the list no longer
 * returns — the list endpoint gives active contacts only. Without it the
 * picker would show an empty box, which reads as "no contact", and blurring an
 * empty picker clears the selection.
 */
function inactiveContact(id: string): CustomerContact {
  return {
    id,
    tenantId: '',
    customerId: '',
    firstName: 'Contact no longer active',
    lastName: '',
    preferredName: null,
    title: null,
    email: null,
    workPhone: null,
    mobile: null,
    address1: null,
    address2: null,
    locality: null,
    region: null,
    country: null,
    postalCode: null,
    primary: false,
    contactType: null,
    ccEmails: [],
    rowStatus: 'inactive',
    createdAt: '',
    updatedAt: '',
  };
}

export type NewContactDraft = { firstName: string; lastName: string; email: string };

/** What was typed into the picker, as a starting point for a new contact:
 *  first word, then the rest — or, if it looks like an address, the email. */
export function draftContactFromQuery(query: string): NewContactDraft {
  const q = query.trim();
  if (q.includes('@')) return { firstName: '', lastName: '', email: q };
  const [first = '', ...rest] = q.split(/\s+/);
  return { firstName: first, lastName: rest.join(' '), email: '' };
}

/**
 * The Bill To block — what prints in the invoice's Bill To box.
 *
 * Every field edits THIS INVOICE's snapshot and never the customer record — an
 * issued invoice has to keep saying what it said. The one exception is adding
 * a new contact, which is saved to the customer on purpose so it can be picked
 * again next month.
 *
 * Picking a contact fills the address, email and CC list at once, with the
 * same shared builder the server uses, so what is on screen is what gets
 * saved. Everything stays editable afterwards.
 */
function BillToFields({
  form,
  customerId,
  contacts,
  contactsLoading,
  contactsError,
  onRetryContacts,
  billingAddress,
  editable,
  canAddContact,
}: {
  form: UseFormReturn<FormValues>;
  customerId: string;
  contacts: CustomerContact[];
  contactsLoading: boolean;
  contactsError: boolean;
  onRetryContacts: () => void;
  /** The customer's billing address, as stored. */
  billingAddress: unknown;
  editable: boolean;
  canAddContact: boolean;
}) {
  const queryClient = useQueryClient();
  const errors = form.formState.errors.billTo;
  const contactId = form.watch('billTo.contactId');
  const [draft, setDraft] = useState<NewContactDraft | null>(null);
  const [draftError, setDraftError] = useState<string | null>(null);

  const missingContact =
    !contactsLoading &&
    !contactsError &&
    contactId !== null &&
    !contacts.some((c) => c.id === contactId);
  const selected =
    contacts.find((c) => c.id === contactId) ??
    (missingContact && contactId !== null ? inactiveContact(contactId) : null);

  const edit = { shouldDirty: true, shouldValidate: true } as const;
  const applyContact = (c: CustomerContact | null) => {
    form.setValue('billTo.contactId', c?.id ?? null, edit);
    if (c === null) {
      // Nobody to mail or copy. The address stays — it is often the
      // company's own — and addresses can still be typed in.
      form.setValue('billTo.recipients', [], edit);
      return;
    }
    form.setValue(
      'billTo.addressLines',
      addressLinesToText(buildBillToAddressLines(billToContactName(c), billingAddress, c)),
      edit,
    );
    // The new contact's addresses replace the old ones: its email as the To,
    // its CC list as CCs. The server applies the same rule.
    form.setValue('billTo.recipients', recipientsFromContact(c), edit);
  };

  const addContact = useMutation({
    mutationFn: (d: NewContactDraft) =>
      customerContactsApi.create(customerId, {
        firstName: d.firstName.trim(),
        lastName: d.lastName.trim(),
        email: d.email.trim() || null,
      }),
    onSuccess: ({ data }) => {
      // Into the cached list first, so the picker can show the new contact
      // before the refetch lands instead of flashing "no longer active".
      const key = queryKeys.customerContacts.list(customerId);
      queryClient.setQueryData<{ data: CustomerContact[] }>(key, (old) =>
        old ? { ...old, data: [...old.data, data] } : old,
      );
      queryClient.invalidateQueries({ queryKey: key });
      setDraft(null);
      setDraftError(null);
      applyContact(data);
      toast.success(`${billToContactName(data) ?? 'Contact'} added to this customer’s contacts`);
    },
    onError: (err) =>
      setDraftError(
        err instanceof ApiError
          ? err.fields?.map((f) => f.message).join(' ') || err.message
          : 'Could not add the contact',
      ),
  });

  return (
    <div className="flex flex-col gap-4">
      {/* A failed load must not read as "this customer has no contacts" —
          clearing the picker would then silently strip the invoice's
          addressee (G15). */}
      {contactsError ? (
        <div className="flex items-center justify-between gap-3 rounded-md border border-destructive/40 p-2.5">
          <p className="text-destructive text-sm">Could not load this customer’s contacts.</p>
          <Button type="button" size="sm" variant="outline" onClick={onRetryContacts}>
            Retry
          </Button>
        </div>
      ) : null}

      {/* The server names `billTo.contactId` when a contact is not this
          customer's. The picker is not a registered input, so the message is
          rendered here explicitly (G12). */}
      <Field
        label="Billing contact"
        htmlFor="invoice-billing-contact"
        error={errors?.contactId?.message}
        hint={missingContact ? 'This contact has since been removed from the customer.' : undefined}
      >
        <Combobox<CustomerContact>
          id="invoice-billing-contact"
          items={contacts}
          value={contactId}
          selectedItem={selected}
          onChange={applyContact}
          getId={(c) => c.id}
          getLabel={contactLabel}
          filterFn={(c, q) => contactLabel(c).toLowerCase().includes(q.toLowerCase())}
          onCreateNew={
            canAddContact && editable
              ? (q) => {
                  setDraftError(null);
                  setDraft(draftContactFromQuery(q));
                }
              : undefined
          }
          createNewLabel="Add a new contact"
          placeholder={contactsLoading ? 'Loading…' : 'Type a name to search or add'}
          emptyText="No contact by that name"
          loading={contactsLoading}
          disabled={!editable || contactsLoading || contactsError}
          hasError={Boolean(errors?.contactId)}
          className="w-full"
        />
      </Field>

      {draft ? (
        <div className="flex flex-col gap-3 rounded-md border bg-muted/40 p-3">
          <p className="font-medium text-sm">New contact for this customer</p>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="First name" htmlFor="new-contact-first-name">
              <Input
                id="new-contact-first-name"
                autoFocus
                value={draft.firstName}
                onChange={(e) => setDraft({ ...draft, firstName: e.target.value })}
              />
            </Field>
            <Field label="Last name" htmlFor="new-contact-last-name">
              <Input
                id="new-contact-last-name"
                value={draft.lastName}
                onChange={(e) => setDraft({ ...draft, lastName: e.target.value })}
              />
            </Field>
            <Field label="Email" htmlFor="new-contact-email" className="sm:col-span-2">
              <Input
                id="new-contact-email"
                type="email"
                placeholder="ap@customer.com"
                value={draft.email}
                onChange={(e) => setDraft({ ...draft, email: e.target.value })}
              />
            </Field>
          </div>
          {draftError ? <p className="text-destructive text-xs">{draftError}</p> : null}
          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              size="sm"
              disabled={
                addContact.isPending ||
                draft.firstName.trim() === '' ||
                draft.lastName.trim() === ''
              }
              onClick={() => addContact.mutate(draft)}
            >
              {addContact.isPending ? <Loader2 className="size-4 animate-spin" /> : null}
              Add contact
            </Button>
            <Button type="button" size="sm" variant="ghost" onClick={() => setDraft(null)}>
              Cancel
            </Button>
            <p className="text-muted-foreground text-xs">
              Saved to the customer’s contacts now, and picked for this invoice.
            </p>
          </div>
        </div>
      ) : null}

      <Field label="Billed to" htmlFor="invoice-bill-to-name" error={errors?.name?.message}>
        <Input
          id="invoice-bill-to-name"
          placeholder="Company name"
          disabled={!editable}
          aria-invalid={Boolean(errors?.name)}
          {...form.register('billTo.name')}
        />
      </Field>

      <Field
        label="Address"
        htmlFor="invoice-bill-to-address"
        error={errors?.addressLines?.message}
      >
        <Textarea
          id="invoice-bill-to-address"
          rows={4}
          placeholder="Attn: Accounts Payable&#10;100 Main St&#10;Plano, TX, 75024"
          disabled={!editable}
          aria-invalid={Boolean(errors?.addressLines)}
          {...form.register('billTo.addressLines')}
        />
      </Field>
    </div>
  );
}

/**
 * How the invoice reaches the customer: who it is emailed to, who is copied,
 * and when. None of it prints. Picking a billing contact in the Bill To block
 * fills the email and CC here — it is one form, so the two stay in step.
 *
 * A missing email is not an error here — an invoice can be raised without one
 * — so it is reported once, in the blockers above the form, as the thing that
 * stops the invoice being sent.
 */
function DeliveryFields({
  form,
  editable,
  sendEditable,
  status,
  addingEmail,
  onAddingEmailChange,
  defaultEmailBody,
  emailSubject,
}: {
  form: UseFormReturn<FormValues>;
  /** To and CC — content, so locked from approval. */
  editable: boolean;
  /** When to send — open until the invoice is sent. */
  sendEditable: boolean;
  /** Null in create mode, where the invoice will be a draft. */
  status: Invoice['status'] | null;
  addingEmail: boolean;
  onAddingEmailChange: (adding: boolean) => void;
  /** The settings template filled in for this invoice, or null while there is
   *  nothing to fill it from (no customer picked yet). */
  defaultEmailBody: string | null;
  emailSubject: string | null;
}) {
  const recipients = form.watch('billTo.recipients');
  const toList = recipientAddresses(recipients, 'to');
  const ccList = recipientAddresses(recipients, 'cc');
  const sendMode = form.watch('sendMode');
  const scheduledSendOn = form.watch('scheduledSendOn');
  const edit = { shouldDirty: true, shouldValidate: true } as const;

  // One stored list, shown as two: To first, then CC. Normalising drops an
  // address typed into one list that the other already holds — it is on the
  // email once either way.
  const setRecipients = (to: readonly string[], cc: readonly string[]) =>
    form.setValue(
      'billTo.recipients',
      normalizeInvoiceRecipients([
        ...to.map((email) => ({ email, kind: 'to' as const })),
        ...cc.map((email) => ({ email, kind: 'cc' as const })),
      ]),
      edit,
    );

  // The server names a bad address by index (`billTo.recipients.2.email`);
  // show it under the list that address sits in. A list-level message (too
  // many recipients) goes under To.
  const recipientErrors = form.formState.errors.billTo?.recipients as unknown as
    | ({ message?: string } & ArrayLike<
        { message?: string; email?: { message?: string } } | undefined
      >)
    | undefined;
  const errorFor = (kind: InvoiceRecipient['kind']): string | undefined => {
    if (!recipientErrors) return undefined;
    const entries = Array.from(recipientErrors);
    const i = entries.findIndex(
      (e, idx) => Boolean(e?.email?.message ?? e?.message) && recipients[idx]?.kind === kind,
    );
    if (i >= 0) return entries[i]?.email?.message ?? entries[i]?.message;
    return kind === 'to' ? recipientErrors.message : undefined;
  };

  return (
    <div className="flex flex-col gap-4">
      <CcEmailsField
        id="invoice-bill-to-email"
        label="To"
        description=""
        addLabel="Add"
        value={toList}
        onChange={(next) => setRecipients(next, ccList)}
        disabled={!editable}
        open={addingEmail}
        onOpenChange={onAddingEmailChange}
        error={errorFor('to')}
      />

      <CcEmailsField
        id="invoice-cc-emails"
        label="CC"
        description=""
        addLabel="Add"
        value={ccList}
        onChange={(next) => setRecipients(toList, next)}
        disabled={!editable}
        error={errorFor('cc')}
      />

      {/* A date, not a time: the sender resolves the moment in the tenant's
          timezone. */}
      <Field
        label="Send"
        htmlFor="invoice-send-mode"
        error={form.formState.errors.scheduledSendOn?.message}
        hint={sendOnHint(sendMode, scheduledSendOn, status)}
      >
        <div className="flex flex-wrap gap-2">
          <Select
            value={sendMode}
            disabled={!sendEditable}
            onValueChange={(v) => {
              const mode = v as SendMode;
              form.setValue('sendMode', mode, { shouldDirty: true });
              if (mode === 'date' && scheduledSendOn === '') {
                form.setValue(
                  'scheduledSendOn',
                  firstSendDate(form.getValues('invoiceDate')),
                  edit,
                );
              } else if (mode !== 'date') {
                form.setValue('scheduledSendOn', '', edit);
              }
            }}
          >
            <SelectTrigger id="invoice-send-mode" className="w-56" aria-label="Send">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SEND_MODES.map((m) => (
                <SelectItem key={m} value={m}>
                  {SEND_MODE_LABELS[m]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {sendMode === 'date' ? (
            <DatePicker
              value={scheduledSendOn}
              onChange={(v) => form.setValue('scheduledSendOn', v, edit)}
              disabled={!sendEditable}
              placeholder="Pick a date"
              ariaInvalid={Boolean(form.formState.errors.scheduledSendOn)}
            />
          ) : null}
        </div>
      </Field>

      <EmailMessageField
        form={form}
        editable={editable}
        defaultEmailBody={defaultEmailBody}
        subject={emailSubject}
        sent={status === 'sent'}
      />
    </div>
  );
}

/**
 * The body of the email. Collapsed to one summary line by default — most
 * invoices go out with the settings message, and the full text would push
 * Attachments below the fold — and opened on request.
 *
 * Open, it shows the message filled in. Until someone edits it, it IS the
 * invoice-settings template rendered from the form — it follows the amount and
 * dates as they change, and nothing is stored. The first keystroke makes it
 * this invoice's own message, sent exactly as written from then on; putting
 * the text back, or Reset, returns it to the template.
 */
function EmailMessageField({
  form,
  editable,
  defaultEmailBody,
  subject,
  sent,
}: {
  form: UseFormReturn<FormValues>;
  /** Content — locked from approval, like the recipients. */
  editable: boolean;
  defaultEmailBody: string | null;
  subject: string | null;
  /** An unedited message is not stored, so once sent the settings template
   *  may no longer say what went out — show nothing rather than today's. */
  sent: boolean;
}) {
  const [open, setOpen] = useState(false);
  const emailBody = form.watch('emailBody');
  const error = form.formState.errors.emailBody?.message;
  const edited = emailBody !== null;
  const edit = { shouldDirty: true, shouldValidate: true } as const;
  // Forced open while it holds an error, so the message has its input to sit
  // under rather than pointing at a collapsed row.
  const expanded = open || Boolean(error);
  const sentUnedited = sent && !edited;
  const unavailable = (!edited && defaultEmailBody === null) || sentUnedited;

  const summary = edited
    ? 'Edited for this invoice'
    : sentUnedited
      ? 'Sent with the invoice settings message at the time; its text is not kept'
      : unavailable
        ? 'Pick a customer to see the message'
        : 'From invoice settings';

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center justify-between gap-2">
        {expanded ? (
          <Label htmlFor="invoice-email-body">Message</Label>
        ) : (
          <span className="font-medium text-sm">Message</span>
        )}
        {!unavailable || edited ? (
          <Button
            type="button"
            variant="link"
            size="sm"
            className="h-auto p-0 text-xs"
            aria-expanded={expanded}
            aria-controls="invoice-email-body-panel"
            onClick={() => setOpen((v) => (error ? true : !v))}
          >
            {expanded ? 'Hide' : editable ? 'View or edit' : 'View'}
          </Button>
        ) : null}
      </div>

      {expanded ? (
        <div id="invoice-email-body-panel" className="flex flex-col gap-1.5">
          {subject ? (
            <p className="truncate text-muted-foreground text-xs" title={subject}>
              Subject: {subject}
            </p>
          ) : null}
          <Textarea
            id="invoice-email-body"
            name="emailBody"
            rows={8}
            className="text-sm"
            disabled={!editable || unavailable}
            aria-invalid={Boolean(error)}
            value={emailBody ?? defaultEmailBody ?? ''}
            // Exact match, not trimmed: a newline typed at the end of the
            // default must not snap straight back to it. The server compares
            // trimmed, so trailing whitespace alone is never stored.
            onChange={(e) =>
              form.setValue(
                'emailBody',
                e.target.value === defaultEmailBody ? null : e.target.value,
                edit,
              )
            }
          />
          <p className="text-muted-foreground text-xs">
            {edited
              ? 'Edited for this invoice — sent exactly as written. Changes to the amount or dates are not reflected.'
              : 'From invoice settings, filled in when it is sent. Edit it to change it for this invoice only.'}
          </p>
          {error ? <p className="text-destructive text-xs">{error}</p> : null}
          {edited && editable && defaultEmailBody !== null ? (
            <Button
              type="button"
              variant="link"
              size="sm"
              className="h-auto self-start p-0 text-xs"
              onClick={() => form.setValue('emailBody', null, edit)}
            >
              Reset to invoice settings message
            </Button>
          ) : null}
        </div>
      ) : (
        <p className="text-muted-foreground text-sm">{summary}</p>
      )}
    </div>
  );
}

/** Per-line validation messages, rendered under the row they belong to rather
 *  than collected into a banner — with ten lines on screen, "description is
 *  required" is useless without knowing which one (G12). */
type LineFieldErrors = Partial<Record<keyof LineValues, { message?: string }>>;

/** A line field's message, under its own input in the row. */
function CellError({ message }: { message?: string }) {
  if (!message) return null;
  return <p className="mt-1 text-destructive text-xs">{message}</p>;
}

/** The line fields whose message renders under their own input. */
function cellKeys(showQtyRate: boolean): ReadonlySet<keyof LineValues> {
  return new Set<keyof LineValues>(
    showQtyRate
      ? ['description', 'quantity', 'rate', 'amount', 'allocations']
      : ['description', 'amount', 'allocations'],
  );
}

function orphanLineMessages(errors: LineFieldErrors | undefined, showQtyRate: boolean): string[] {
  if (!errors) return [];
  const inCell = cellKeys(showQtyRate);
  return (Object.entries(errors) as Array<[keyof LineValues, { message?: string } | undefined]>)
    .filter(([key]) => !inCell.has(key))
    .map(([, e]) => e?.message)
    .filter((m): m is string => Boolean(m));
}

function hasLineErrors(errors: LineFieldErrors | undefined, showQtyRate: boolean): boolean {
  return orphanLineMessages(errors, showQtyRate).length > 0;
}

/** A line's messages that have no input on screen to sit under. */
function LineErrors({ errors, showQtyRate }: { errors?: LineFieldErrors; showQtyRate: boolean }) {
  const messages = orphanLineMessages(errors, showQtyRate);
  if (messages.length === 0) return null;
  return <p className="text-destructive text-xs">{messages.join(' · ')}</p>;
}

function Total({
  label,
  value,
  emphasis = false,
}: {
  label: string;
  value: string;
  emphasis?: boolean;
}) {
  return (
    <div className={`flex justify-between ${emphasis ? 'font-semibold' : ''}`}>
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="tabular-nums">{value}</dd>
    </div>
  );
}

/** Totals come back from `computeInvoiceTotals` as canonical two-decimal
 *  strings, so this only has to format — no `Number('')` guard, unlike the
 *  list page's `formatMoney`, which handles raw wire values. */
function formatCurrency(value: string, currency: string): string {
  const n = Number(value);
  if (!Number.isFinite(n)) return value;
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(n);
  } catch {
    return `${currency} ${value}`;
  }
}

function DetailSkeleton() {
  return (
    <PageShell>
      <div className="flex flex-col gap-6" aria-busy="true">
        <Skeleton className="h-4 w-24" />
        <Skeleton className="h-8 w-48" />
        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_22rem]">
          <div className="flex flex-col gap-4">
            <Skeleton className="h-28 w-full" />
            <Skeleton className="h-40 w-full" />
            <Skeleton className="h-56 w-full" />
          </div>
          <Skeleton className="h-[32rem] w-full" />
        </div>
      </div>
    </PageShell>
  );
}
